import { z } from "zod";
import { hasDisallowedPersonNameChars, normalizePersonNameInput } from "@goppy/domain";
// US-E6.F9-0412 (@UC0060): trạng thái của MỘT khoản phải đóng — TÁI DÙNG enum đã có ở collections,
// KHÔNG khai lại 7 giá trị lần thứ hai (drift whitelist là họ bệnh đắt nhất của repo này).
import { memberDueStatusSchema, paymentConfirmationTargetSchema } from "./collections.js";
// US-E23.F1-0867 (@UC0048): cờ `canExportFinanceReport` dẫn xuất từ CÙNG tập kind mà `ReportsService` gác cho member.
import { GROUP_REPORT_KIND, MEMBER_EXPORTABLE_GROUP_REPORT_KINDS } from "./reports.js";

/**
 * Shared DTO cho Groups — POST /v1/groups (US-E4.F1-0018 / @UC0011).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 3: path canonical = `/v1/groups`. Slice 0018 = TỐI THIỂU:
 * chỉ tạo nhóm + creator auto Owner. Invitations/join/transfer/role-management → story E4 sau.
 *
 * Bảo mật (C2/C3): response KHÔNG mang field nhạy cảm — chỉ field cần cho UI điều hướng.
 */

/** Giới hạn độ dài tên nhóm (US-E4.F1-0018 / @UC0011 — invariant story ≤50 ký tự, AC3). */
export const GROUP_NAME_MAX_LENGTH = 50;

/** Thông điệp lỗi khi tên nhóm trống (AC2) — một nguồn sự thật api ↔ mobile. */
export const GROUP_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên nhóm";

/** Thông điệp lỗi khi tên nhóm vượt 50 ký tự (AC3). */
export const GROUP_NAME_TOO_LONG_MESSAGE = "Tên nhóm tối đa 50 ký tự";

/**
 * Body cho POST /v1/groups.
 * - name BẮT BUỘC: trim trước rồi validate 1..50 ký tự (đếm SAU trim).
 *   Rỗng/chỉ-khoảng-trắng → reject (AC2); >50 → reject (AC3).
 * - `.strict()` chặn field lạ tại biên (chống IDOR/over-post: KHÔNG nhận role/owner/id từ body —
 *   creator auto Owner do server quyết, client KHÔNG được tự gán).
 */
/**
 * US-E4.F1-0388-S01 (@UC0055) — category là DANH MỤC ĐỘNG (bảng `group_categories`), thay 6 nhãn
 * đóng của US-E23.F1-0331-S08.
 *
 * 🔴 QUAN HỆ MỚI giữa `category` và `type` (QĐ PO 12/08/2026, đảo QĐ cũ có ghi lý do):
 *  · `type` (`standard|parent`) vẫn là **LUẬT ánh xạ danh tính** — không đổi nghĩa.
 *  · Nhưng mỗi category trong bảng GẮN SẴN một `type` — server SUY `Group.type` từ category lúc
 *    tạo nhóm, client KHÔNG gửi `type` rời (hết ca cặp (type, category) mâu thuẫn nhau).
 *  · QĐ cũ "không suy cái này từ cái kia" tồn tại vì `other`/`hometown` mơ hồ — nay `other` tách
 *    thành `individual_person`/`family_couple`, mọi category đều có type xác định ⇒ lý do gốc hết.
 *
 * Contract chỉ còn gác **HÌNH DẠNG** code (snake_case) tại biên; tính hợp lệ NGỮ NGHĨA (code tồn
 * tại? đang `enabled`?) do FK + service quyết — closed-set sống trong DB, không trong file này.
 */
export const GROUP_CATEGORY_CODE_REGEX = /^[a-z][a-z0-9_]{1,49}$/;

export const groupCategorySchema = z
  .string()
  .regex(GROUP_CATEGORY_CODE_REGEX, "category phải là code snake_case (vd parent_class)");

export type GroupCategory = z.infer<typeof groupCategorySchema>;

/** Luật ánh xạ danh tính gắn cho category — đúng 2 giá trị, không nhận thứ ba. */
export const groupCategoryTypeSchema = z.enum(["standard", "parent"]);
export type GroupCategoryType = z.infer<typeof groupCategoryTypeSchema>;

/**
 * US-E4.F1-0431 (@UC9677) — TỪ VỰNG HIỂN THỊ theo loại nhóm.
 *
 * 24 khoá ARB của mobile đang ghim cứng "học sinh"/"phụ huynh" và hiện ở MỌI nhóm — hội đồng hương
 * cũng bị nhắc *"ghi chú cho phụ huynh về chi phí cho học sinh"*. Server cấp DANH TỪ, ARB giữ khung
 * câu và bơm danh từ vào.
 *
 * 🔴 KHOÁ THEO Ô NGHĨA, KHÔNG THEO MÀN. Sáu ô dưới đây phủ cả 24 nhãn; `seat_noun` một mình xuất
 * hiện ở 14 nhãn qua 3 màn. Khoá theo màn (`GROUP_DETAIL.*`, `ADD_MEMBER.*`) thì cùng một danh từ bị
 * CHÉP vào từng màn rồi trôi lệch — mẫu đề xuất ban đầu đã lệch ngay trong chính nó
 * ("Học sinh tên là" vs "Học sinh tên là..."). Thêm màn mới dùng lại `seat_noun` ⇒ 0 thay đổi ở đây.
 *
 * 🔴 `seat` / `representative`, KHÔNG `primary` / `dependent`. `primary_` đụng `isPrimaryContact`
 * và `primaryUserId` vốn nghĩa "đại diện CHÍNH" — khái niệm khác hẳn. Và phụ huynh không "phụ
 * thuộc" suất: SUẤT là thứ gánh nghĩa vụ tiền, phụ huynh ĐẠI DIỆN cho nó.
 *
 * 🔴 KHÔNG có cờ `has_dependent`: suy từ `type`. Hai nguồn cho một câu hỏi là mẫu bug lặp nhiều
 * nhất repo này.
 */
export const categoryLabelValueSchema = z
  .object({
    vi: z.string().min(1).max(120),
    en: z.string().min(1).max(120),
  })
  .strict();
export type CategoryLabelValue = z.infer<typeof categoryLabelValueSchema>;

export const categoryLabelsSchema = z
  .object({
    seat_noun: categoryLabelValueSchema.describe(
      "Danh từ chỉ NGƯỜI/THỰC THỂ mà SUẤT đại diện — thứ gánh nghĩa vụ tiền. Số ít, không viết hoa " +
        'toàn bộ, không mạo từ, không kèm ngữ cảnh. ĐÚNG: "Học sinh" · "Gia đình" · "Bé". ' +
        'SAI: "Các học sinh" (số nhiều) · "HS" (viết tắt) · "Học sinh lớp 1A" (kèm ngữ cảnh).',
    ),
    representative_noun: categoryLabelValueSchema
      .describe(
        "Danh từ chỉ NGƯỜI ĐẠI DIỆN cho suất. CHỈ dùng khi type=parent. KHÔNG hardcode 'Phụ huynh' " +
          'trong đầu: family_travel/family_couple thì người đại diện KHÔNG phải phụ huynh. ' +
          'ĐÚNG: "Phụ huynh" · "Người đại diện" · "Người giám hộ". SAI: "Bố mẹ học sinh" (kèm suất) · "PH".',
      )
      .optional(),
    seat_name_example: categoryLabelValueSchema
      .describe('Ví dụ TÊN của suất, hiện làm hint ô nhập. ĐÚNG: "Ví dụ: Nguyễn Văn A".')
      .optional(),
    representative_name_example: categoryLabelValueSchema
      .describe('Ví dụ TÊN người đại diện. ĐÚNG: "Ví dụ: Ba bé Long".')
      .optional(),
    birth_date_helper: categoryLabelValueSchema
      .describe("Câu giải thích vì sao ô ngày sinh bị khoá khi chọn suất CÓ SẴN.")
      .optional(),
    collection_desc_example: categoryLabelValueSchema
      .describe("Ví dụ mô tả đợt thu. 🔴 Bản ARB mặc định phải TRUNG TÍNH — ô này mới nêu chi phí đặc thù.")
      .optional(),
  })
  .strict();
export type CategoryLabels = z.infer<typeof categoryLabelsSchema>;

/**
 * US-E4.F1-0431 — cột `group_categories.labels` là JSONB ⇒ tầng đọc nhận `unknown`. Đây là CỬA
 * DUY NHẤT biến nó thành `CategoryLabels` cho response (group profile · members · invite preview ·
 * admin dictionary), để không ai `as CategoryLabels` thẳng tay.
 *
 * 🔴 FAIL-SOFT có chủ đích: dữ liệu lệch hình dạng (import tay, restore một phần) ⇒ trả `undefined`
 * ⇒ client rơi về ARB mặc định trung tính — ĐÚNG MỘT hành vi fallback như mọi ca thiếu pack khác
 * (task index §8). Ném 500 cả màn hình vì một cái nhãn trang trí là đổi lỗi hiển thị lấy lỗi chặn
 * đường; và admin vẫn sửa lại được bằng `PATCH /v1/admin/group-categories/:code` (@UC9678).
 * Hình dạng đã được gác hai lớp trước đó: zod ở biên ghi + CHECK `group_categories_labels_shape_check`.
 */
export function parseCategoryLabels(raw: unknown): CategoryLabels | undefined {
  if (raw === null || raw === undefined) return undefined;
  const parsed = categoryLabelsSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/**
 * US-E4.F1-0431-S03 — LUẬT `type` ↔ `labels` viết ĐÚNG MỘT LẦN.
 *
 * 🔴 Ba nơi cần cùng luật này: object `category` nhúng trong response (dưới đây), body
 * `POST /v1/admin/group-categories`, và service `PATCH /v1/admin/group-categories/:code` (nơi
 * `type` KHÔNG có trong body nên phải đối chiếu với row đang có ⇒ không dùng zod được).
 * Chép luật ra ba chỗ là mẫu "hai nguồn cho một câu hỏi" mà repo này đã trả giá nhiều lần: sửa
 * một chỗ quên hai chỗ ⇒ biên chặn còn service cho qua (hoặc ngược lại). Hàm THUẦN, dùng được cả
 * trong zod lẫn ngoài zod.
 *
 * Trả về câu giải thích khi vi phạm, `null` khi hợp lệ.
 */
export function categoryLabelsTypeViolation(
  type: GroupCategoryType,
  labels: CategoryLabels | null | undefined,
): string | null {
  if (!labels) return null;
  // 🔴 Trạng thái sai KHÔNG BIỂU DIỄN ĐƯỢC (CLAUDE.md §7): nhóm standard không có khái niệm
  // "người đại diện" nên gửi `representative_noun` là mô tả một thứ không tồn tại.
  if (type === "standard" && labels.representative_noun) {
    return "type=standard KHÔNG có người đại diện — bỏ representative_noun";
  }
  // Có `labels` cho nhóm parent thì phải đủ HAI danh từ nền: thiếu một cái là màn hiện nửa đúng
  // nửa mặc định, khó phát hiện hơn hẳn so với thiếu cả gói.
  if (type === "parent" && !labels.representative_noun) {
    return "type=parent có labels thì BẮT BUỘC representative_noun";
  }
  return null;
}

/** Đường dẫn issue chung cho vi phạm `type` ↔ `labels` — client trỏ đúng ô để sửa. */
export const CATEGORY_LABELS_ISSUE_PATH = ["labels", "representative_noun"] as const;

/** Bọc `categoryLabelsTypeViolation` thành refinement zod — dùng cho MỌI schema có cả type+labels. */
export function refineCategoryLabelsByType(
  value: { readonly type: GroupCategoryType; readonly labels?: CategoryLabels },
  ctx: z.RefinementCtx,
): void {
  const message = categoryLabelsTypeViolation(value.type, value.labels);
  if (message) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...CATEGORY_LABELS_ISSUE_PATH],
      message,
    });
  }
}

/**
 * Object `category` nhúng trong các response nhóm. Tách thành export CÓ TÊN (US-E4.F1-0431) để
 * ba response dùng CHUNG một hình dạng và để test đo được ràng buộc mà không phải dựng cả
 * `groupProfileResponse` — dựng thiếu field ở wrapper sẽ làm test âm XANH VÌ LÝ DO SAI.
 */
export const groupCategoryEmbeddedSchema = z
    .object({
    code: groupCategorySchema,
    /** Luật ánh xạ danh tính của CHÍNH nhóm này — dùng lại enum đã có, không khai chuỗi tự do. */
    type: groupCategoryTypeSchema,
    nameVi: z.string().min(1),
    nameEn: z.string().min(1),
    /**
     * US-E4.F1-0431 (@UC9677). VẮNG MẶT là hợp lệ và có nghĩa: client rơi về ARB mặc định
     * (trung tính). KHÔNG dùng `null` — "chưa cấu hình" và "cấu hình rỗng" phải cùng một hành vi,
     * hai cách biểu diễn cho một trạng thái là mời gọi hai nhánh xử lý.
     */
    labels: categoryLabelsSchema.optional(),
  })
    .strict()
    .superRefine(refineCategoryLabelsByType);
export type GroupCategoryEmbedded = z.infer<typeof groupCategoryEmbeddedSchema>;

/**
 * US-E4.F1-0431-S02 — MỘT cửa dựng object `category` cho CẢ BA response (`GET /groups/:id`,
 * `GET /groups/:id/members`, `POST /groups/:id/invitations/preview`).
 *
 * 🔴 Ba chỗ chứ không một, và đây là lý do (đừng gộp lại): *Chi tiết thành viên* của mobile dựng
 * dữ liệu từ `/groups/:id/members` chứ không có endpoint riêng; còn luồng MỜI chiếm 14/24 nhãn và
 * xảy ra TRƯỚC khi vào nhóm — người được mời chưa là member nên guard của `/groups/:id` trả 404.
 * Ba nơi dựng tay ⇒ ba hình dạng trôi lệch, nên chúng gọi CÙNG hàm này.
 *
 * 🔴 `groupType` là `groups.type` — LUẬT của CHÍNH NHÓM, KHÔNG phải `group_categories.type`
 * (US-E4.F1-0407 AC4b): nhãn là của danh mục, luật là của nhóm, và hai thứ đó lệch nhau được
 * trong dữ liệu thật. `members.service.ts` chặn tạo suất bằng cột này, nên client phải nhận đúng
 * cột này.
 *
 * 🔴 FAIL-SOFT ba nhánh, tất cả rơi về CÙNG một hành vi (ARB mặc định trung tính — task index §8):
 *   · không có danh mục ⇒ `null` (KHÔNG object rỗng: "chưa phân loại" phải phân biệt được với
 *     "có loại mà thiếu nhãn");
 *   · `groupType` ngoài whitelist (dữ liệu bẩn) ⇒ `null` — bịa `'standard'` sẽ khoá nhầm hành vi
 *     client ở một nhóm parent thật;
 *   · gói lệch hình dạng HOẶC không hợp với luật của nhóm ⇒ BỎ GÓI, giữ nhãn. Trả nguyên gói lệch
 *     sẽ tạo ra response vi phạm chính schema của nó ⇒ client `.strict()` vỡ CẢ MÀN vì một danh từ
 *     trang trí.
 */
export function buildGroupCategoryEmbedded(
  groupType: string,
  category:
    | { readonly code: string; readonly nameVi: string; readonly nameEn: string; readonly labels?: unknown }
    | null
    | undefined,
): GroupCategoryEmbedded | null {
  if (!category) return null;
  const parsedType = groupCategoryTypeSchema.safeParse(groupType);
  if (!parsedType.success) return null;
  const type = parsedType.data;

  const labels = parseCategoryLabels(category.labels);
  const usable = labels && !categoryLabelsTypeViolation(type, labels) ? labels : undefined;

  return {
    code: category.code,
    type,
    nameVi: category.nameVi,
    nameEn: category.nameEn,
    ...(usable ? { labels: usable } : {}),
  };
}


/**
 * Item catalog cho END-USER (`GET /v1/group-categories`): chỉ danh mục `enabled`, KHÔNG lộ
 * `status`/`id` — trạng thái quản trị không phải việc của client tạo nhóm. Server cấp nhãn song
 * ngữ vì danh mục động: client không thể ARB-map một code admin vừa tạo.
 */
export const publicGroupCategoryItemSchema = z
  .object({
    code: groupCategorySchema,
    type: groupCategoryTypeSchema,
    nameVi: z.string().min(1),
    nameEn: z.string().min(1),
  })
  .strict();
export type PublicGroupCategoryItem = z.infer<typeof publicGroupCategoryItemSchema>;

export const groupCategoriesResponseSchema = z
  .object({ data: z.array(publicGroupCategoryItemSchema) })
  .strict();
export type GroupCategoriesResponse = z.infer<typeof groupCategoriesResponseSchema>;

/** Mô tả nhóm (`Group.purpose`). Figma cho nhập mô tả khi tạo/sửa nhóm. */
export const GROUP_PURPOSE_MAX_LENGTH = 200;

export const createGroupRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, GROUP_NAME_REQUIRED_MESSAGE)
      .max(GROUP_NAME_MAX_LENGTH, GROUP_NAME_TOO_LONG_MESSAGE),
    /**
     * US-E4.F1-0388-S03 (QĐ PO 12/08/2026): `type` ĐÃ GỠ khỏi request — server SUY từ category
     * (bảng `group_categories` gắn sẵn type cho mỗi code). Client gửi `type` ⇒ 400 (.strict()).
     * Cùng logic chống-mâu-thuẫn của S08 cũ (không nhận mode rời) đẩy lên một nấc: không nhận
     * cả type rời — một nguồn quyết định duy nhất là category.
     * Vắng category ⇒ NULL + type `standard` (0 regress cho client chỉ gửi `{name}`).
     * Category paused/không tồn tại ⇒ 422 GROUP_CATEGORY_INVALID (service, fail-closed).
     */
    category: groupCategorySchema.optional(),
    purpose: z.string().trim().max(GROUP_PURPOSE_MAX_LENGTH).optional(),
  })
  .strict();

export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

/**
 * US-E23.F1-0331-S07 (@UC9512) — `PATCH /v1/groups/:id`.
 *
 * 🔴 CHỈ 3 field hồ sơ. KHÔNG có `defaultCurrency`: đổi đơn vị tiền của nhóm đã có sổ cái làm mọi
 * con số cũ mang nghĩa khác mà không bút toán nào giải thích (ledger ĐƠN-CURRENCY, ADR-0005).
 * KHÔNG có `type`: đổi luật ánh xạ danh tính giữa đời sống một nhóm sẽ mồ côi các MembershipAccount
 * đã tạo theo luật cũ.
 *
 * `.strict()` + `.refine` ít nhất 1 field: PATCH rỗng là request vô nghĩa, chặn ở biên thay vì để
 * service trả về "đã cập nhật" mà không cập nhật gì.
 */
export const patchGroupRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, GROUP_NAME_REQUIRED_MESSAGE)
      .max(GROUP_NAME_MAX_LENGTH, GROUP_NAME_TOO_LONG_MESSAGE)
      .optional(),
    /** `null` = xoá mô tả (tường minh), khác `undefined` = không đổi. */
    purpose: z.string().trim().max(GROUP_PURPOSE_MAX_LENGTH).nullable().optional(),
    avatarUrl: z.string().url().nullable().optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, {
    message: "Cần ít nhất một thông tin để cập nhật",
  });

export type PatchGroupRequest = z.infer<typeof patchGroupRequestSchema>;

/** Response `PATCH /groups/:id` + `POST /groups/:id/archive|unarchive`. */
export const groupProfileUpdateResponseSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    purpose: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  })
  .strict();

export type GroupProfileUpdateResponse = z.infer<typeof groupProfileUpdateResponseSchema>;

/**
 * Response `PUT /v1/groups/:id/avatar` (US-E24.F32-0446 · @UC9714) — upload ảnh đại diện NHÓM.
 *
 * 🔴 `avatarUrl` là **URL DÙNG ĐƯỢC NGAY** (public base nếu có, else presigned GET), KHÔNG phải
 * object key. Cột DB lưu key — bền vững; API trả URL — dùng được. Hai thứ khác nhau và client chỉ
 * bao giờ thấy thứ thứ hai.
 *
 * 🔴 `.url()` chứ không `.min(1)`: nếu một ngày server lỡ trả thẳng key (`group-avatars/…`) thì
 * `parse` NÉM ngay ở test contract, thay vì client âm thầm gán vào `<img src>` và hiện ảnh vỡ.
 * Đây là chốt chặn RẺ nhất cho đúng lỗi mà `group-avatar-resolve.invariants.spec.ts` sinh ra để chặn.
 * (`fundQrUploadResponseSchema` dùng `.min(1)` — ở đây cố ý chặt hơn, không phải quên đồng bộ.)
 */
export const groupAvatarUploadResponseSchema = z
  .object({
    avatarUrl: z.string().url(),
  })
  .strict();

export type GroupAvatarUploadResponse = z.infer<typeof groupAvatarUploadResponseSchema>;

export const groupArchiveResponseSchema = z
  .object({
    id: z.string().uuid(),
    /** ISO-8601 khi đã lưu trữ; `null` khi đang mở. */
    archivedAt: z.string().datetime().nullable(),
  })
  .strict();

export type GroupArchiveResponse = z.infer<typeof groupArchiveResponseSchema>;

/**
 * US-E24.F29-0432-S06 (`@UC0026`) — `DELETE /v1/groups/:id` (soft-delete).
 *
 * 🔴 SCHEMA RIÊNG, KHÔNG tái dùng `groupArchiveResponseSchema`. Hai hành động khác nhau ở đúng chỗ
 * quan trọng nhất — archive ĐẢO ĐƯỢC và giữ đường trả nợ sống; xoá thì không đảo được trong app và
 * khoá luôn đường trả nợ. Dùng chung một shape sẽ khiến client `switch` nhầm trường và báo sai cho
 * người dùng về một việc không hoàn tác được.
 */
export const groupSoftDeleteResponseSchema = z
  .object({
    id: z.string().uuid(),
    /** ISO-8601 thời điểm xoá. Không bao giờ `null` ở response thành công — nhưng giữ nullable cho
     * khớp cột DB, chốt shape là việc của `.strict()` chứ không phải của một phép `as`. */
    deletedAt: z.string().datetime().nullable(),
  })
  .strict();

export type GroupSoftDeleteResponse = z.infer<typeof groupSoftDeleteResponseSchema>;

/**
 * US-E23.F1-0331-S06 (@UC9511) — code 409 khi WRITE nghiệp vụ trên nhóm ĐÃ LƯU TRỮ.
 *
 * 🔴 CỐ Ý KHÁC `GROUP_SUSPENDED_CODE`: đình chỉ là admin hệ thống cưỡng chế (tạm thời), lưu trữ là
 * chính nhóm tự đóng sổ (quyết định cuối vòng đời). Gộp hai mã lại buộc client nói một câu mơ hồ
 * *"nhóm bị khoá"* trong khi cách xử lý của người dùng hoàn toàn khác nhau: đình chỉ thì liên hệ hỗ
 * trợ, lưu trữ thì owner tự mở lại được.
 *
 * READ **không** bị chặn — xem lại toàn bộ sổ của nhóm đã đóng chính là điểm của "lưu trữ thay xoá".
 * Đường TRẢ NỢ cũ cũng không bị chặn (freeze chỉ gắn ở `/funds/:id/**` — nơi sinh nghĩa vụ MỚI).
 */
export const GROUP_ARCHIVED_CODE = "GROUP_ARCHIVED" as const;
export const GROUP_ARCHIVED_MESSAGE =
  "Nhóm đã lưu trữ — không tạo được đợt thu hay khoản chi mới. Mở lại nhóm nếu cần tiếp tục.";

/**
 * Role của member trong nhóm (Phase 3 RBAC). Một nguồn sự thật cho api + mobile + task E4 sau.
 * M5: tránh magic-string ('owner'/'admin'/...) rải rác — dùng hằng GROUP_ROLE.* / GroupRole type.
 */
export const GROUP_ROLE = {
  OWNER: "owner",
  ADMIN: "admin",
  TREASURER: "treasurer",
  MEMBER: "member",
} as const;

/**
 * 🔴🔴 US-E5.F2-0598-S06 (@UC0057) — **VAI ĐƯỢC DUYỆT LỜI KHAI ĐÃ CHUYỂN TIỀN.**
 *
 * MỘT nguồn cho hai chỗ, và đó là toàn bộ lý do hằng này tồn tại:
 *   1. `POST /v1/payment-confirmations/:id/{approve,decline}` — ai bấm duyệt được;
 *   2. luật **tự-duyệt** của đường tự khai (`actorCanSelfApprove`) — ai khai xong vào sổ ngay.
 *
 * Hai chỗ đó PHẢI trùng nhau: người tự duyệt được cho chính mình đúng là người đã duyệt được cho
 * người khác. Khai hai danh sách là dựng lại bẫy "hai whitelist trôi khỏi nhau" mà repo đã trả giá ở
 * `txType` — và ở đây hậu quả là **một người bơm được số dư bằng lời tự khai của chính họ**.
 *
 * ⚠️ Thêm/bớt vai ở đây là đổi luật tiền ⇒ `@gate`, approver ≠ tác giả.
 */
export const PAYMENT_APPROVER_ROLES = [
  GROUP_ROLE.OWNER,
  GROUP_ROLE.ADMIN,
  GROUP_ROLE.TREASURER,
] as const;

export const groupRoleSchema = z.enum([
  GROUP_ROLE.OWNER,
  GROUP_ROLE.ADMIN,
  GROUP_ROLE.TREASURER,
  GROUP_ROLE.MEMBER,
]);
export type GroupRole = z.infer<typeof groupRoleSchema>;

/**
 * Trạng thái membership (Phase 3). Một nguồn sự thật cho api + task E4 sau (M5).
 * Khớp schema.prisma GroupMember.status: active | invited | join_pending | removed | left | unclaimed.
 *
 * 🔴🔴 US-E24.F3-0352 (@UC9573) — `unclaimed` VÀO ĐÂY, thôi khai tay.
 *
 * TRƯỚC ĐÓ: `unclaimed` (US-IDM — "chỗ ngồi" chưa có người đại diện: học sinh admin import, hoặc suất
 * phụ huynh vừa tạo) tồn tại THẬT trong DB và trong `schema.prisma:508`, nhưng **KHÔNG** có ở enum này
 * ⇒ được khai bằng **magic string ở 2 file** (`identity-selection.service.ts`,
 * `member-account-mapping.service.ts`), mỗi file một bản. Đúng tiền lệ *"drift 2 whitelist"* của repo
 * (§3.8): hai danh sách rời nhau thì sớm muộn lệch nhau, và lệch ở đây nghĩa là **một suất có nghĩa vụ
 * tiền lọt khỏi mọi bộ lọc** — bug #5 sống được chính vì không danh sách nào biết `unclaimed` là gì.
 *
 * 🔴 Nới enum ở đây là **nới cái ĐƯỢC PHÉP BIỂU DIỄN**, KHÔNG nới cái được LỌC MẶC ĐỊNH: các đường đọc
 * (`GET /groups/:id/members`, `listMyGroups`, `countActiveMembers`) vẫn chốt `ACTIVE` như cũ. Xem
 * `memberListStatusFilterSchema` — muốn thấy `unclaimed` thì phải HỎI TƯỜNG MINH.
 */
export const GROUP_MEMBER_STATUS = {
  ACTIVE: "active",
  INVITED: "invited",
  JOIN_PENDING: "join_pending",
  REMOVED: "removed",
  LEFT: "left",
  /** "Chỗ ngồi" chưa có người đại diện (US-IDM ghost import / suất phụ huynh vừa tạo). */
  UNCLAIMED: "unclaimed",
  /**
   * 🔴 US-E4.F2-0597 (@UC0056 @UC0027) — hàng chờ ĐÃ ĐÓNG vì người này ĐƯỢC GẮN vào một suất khác.
   *
   * Nhóm `parent`: tư cách thành viên thuộc về SUẤT (đứa trẻ), phụ huynh chỉ là người đại diện. Khi
   * phụ huynh được gắn vào suất, hàng chờ `join_pending` của chính họ phải đóng lại — nếu không họ
   * nằm mãi trong danh sách chờ dù đã vào nhóm rồi.
   *
   * Trước 0597 chỗ này mượn `left`, khiến `left` mang HAI nghĩa trái ngược ("đã rời" vs "đã vào
   * thành công"). `claimed` trả `left` về đúng một nghĩa.
   *
   * 🔴 BA NƠI PHẢI KHAI CÙNG LÚC, thiếu một là hỏng luồng phụ huynh:
   *  1. enum này + `groupMemberStatusSchema`;
   *  2. CHECK `group_members_status_check` ở DB — thiếu ⇒ ghi vào là lỗi ràng buộc;
   *  3. 🔴 danh sách LOẠI TRỪ của partial-unique `one_active_seat_per_user_per_group` — thiếu ⇒ suất
   *     đã đóng VẪN CHIẾM Ô ⇒ phụ huynh **không gắn được vào suất thứ hai**. Đây chính là lý do chỗ
   *     đó phải mượn `left` ngay từ đầu (`left` nằm sẵn trong danh sách loại trừ).
   *
   * KHÔNG mượn `unclaimed`: nó nghĩa là *ghế chưa có người*, ngược hẳn — và nó **chiếm ô**.
   */
  CLAIMED: "claimed",
} as const;

/**
 * 🔴🔴 US-E4.F2-0597 (review PR #676 M5) — TRẠNG THÁI **THÔI CHIẾM Ô** suất-duy-nhất.
 *
 * Đây là vế bù của partial-unique `one_active_seat_per_user_per_group`
 * (`WHERE status NOT IN (...) AND "primaryUserId" IS NOT NULL`). Ba giá trị này mô tả một suất đã
 * KẾT THÚC vòng đời, nên cùng một người được giữ suất khác trong cùng nhóm.
 *
 * 🔴 VÌ SAO PHẢI LÀ MỘT HẰNG DÙNG CHUNG: danh sách này bị chép tay ở **ba** nơi (`src/` hai chỗ
 * `notIn`, migration một chỗ `WHERE`). Lệch một giá trị thì hỏng theo hai chiều trái ngược — thiếu ⇒
 * `create` đâm vào index (P2002 giữa một lần nhập hàng loạt); thừa ⇒ bỏ qua việc tạo suất, người
 * trong file nhập im lặng không vào nhóm. Phép đo đọc `WHERE` từ `pg_index` rồi so với **hằng này**,
 * nên drift ở `src/` hay ở migration đều đỏ tại một chỗ.
 */
export const SEAT_SLOT_EXCLUDED_STATUSES = [
  GROUP_MEMBER_STATUS.REMOVED,
  GROUP_MEMBER_STATUS.LEFT,
  GROUP_MEMBER_STATUS.CLAIMED,
] as const;

export const groupMemberStatusSchema = z.enum([
  GROUP_MEMBER_STATUS.ACTIVE,
  GROUP_MEMBER_STATUS.INVITED,
  GROUP_MEMBER_STATUS.JOIN_PENDING,
  GROUP_MEMBER_STATUS.REMOVED,
  GROUP_MEMBER_STATUS.LEFT,
  GROUP_MEMBER_STATUS.UNCLAIMED,
  GROUP_MEMBER_STATUS.CLAIMED,
]);
export type GroupMemberStatus = z.infer<typeof groupMemberStatusSchema>;

/**
 * US-E24.F3-0352-S03 (@UC9574) — tập trạng thái LỌC ĐƯỢC ở `GET /v1/groups/:id/members`.
 *
 * 🔴 **Vắng tham số ⇒ giữ nguyên `active`.** Đổi mặc định thành "trả cả unclaimed" là **thay đổi phá
 * vỡ** cho mọi client đang đếm sĩ số (thủ quỹ đếm đầu người ra một con số khác sau một lần deploy).
 * Suất kẹt phải NHÌN THẤY ĐƯỢC — nhưng bằng cách HỎI, không bằng cách đổi câu trả lời mặc định.
 *
 * Cố ý **KHÔNG** mở `removed`/`left`/`invited`/`join_pending` ở đây: chúng không phải "suất đang gánh
 * nghĩa vụ tiền", và mở rộng bộ lọc là mở rộng bề mặt rò danh sách người đã rời nhóm.
 */
/**
 * 🔴 US-E4.F1-0411 (QĐ người dùng 14/08/2026) — KHÔNG phải một trạng thái mà là một **TẬP**:
 * mọi SUẤT ĐANG SỐNG (`active` + `unclaimed`), tức đã có hoặc chưa có người đại diện.
 *
 * Vì sao là một giá trị riêng chứ không bắt client gửi `?status=active,unclaimed`: "suất đang sống
 * gồm những trạng thái nào" là một LUẬT NGHIỆP VỤ. Client tự liệt kê thì ngày enum nới thêm một
 * trạng thái, mọi client đều mục — đúng hai lần repo này đã dính (`unclaimed` thiếu ở enum client,
 * `join_pending` thiếu ở `toFilterValue`). Server giữ luật, client chỉ hỏi.
 *
 * Loại `join_pending`/`left`/`removed`: đó là người ĐÃ RỜI hoặc BỊ GỠ. Danh sách thành viên là
 * danh sách người đang gánh nghĩa vụ, không phải lịch sử ai từng ở đây.
 */
export const MEMBER_LIST_STATUS_SEATS = "seats" as const;

/**
 * 🔴 US-E4.F4-0595 (review PR #675 HIGH-B) — *"ĐANG XẾP HÀNG XIN VÀO"*, tức ĐÚNG tập mà mục ghim
 * "THÀNH VIÊN ĐANG CHỜ XÉT DUYỆT" hiển thị và `pendingTotalCount` đếm.
 *
 * ## Vì sao cần một giá trị RIÊNG, không dùng lại `join_pending`
 * `join_pending` là **một cột**; vị-từ thật là **ba** (`QUEUEING_SEAT_WHERE`: `status` +
 * `joinedAt IS NULL` + `leftAt IS NULL`), vì cùng giá trị đó cũng được `selfLeave` ghi cho người
 * **đã rời** (QĐ PO 13/08 #4). Màn *"Xem tất cả N yêu cầu"* mà hỏi `?status=join_pending` sẽ:
 *  1. hiện nhiều dòng hơn con số N mà nút vừa hứa; và
 *  2. mở một đường bấm-là-duyệt cho người đã rời — `approvePending` chỉ khớp `status`, nên nó ghi
 *     đè `joinedAt`, xoá `leftAt`, rồi `autoEnroll` **sinh nghĩa vụ tiền** cho người đã đi ra.
 *
 * Bản vá đầu chặn ở CLIENT bằng `joinedAt == null`. Review đo được nó **fail-OPEN**: ghế `unclaimed`
 * (vốn `joinedAt = null`) rồi rời vẫn lọt — đúng ca mà docblock `QUEUEING_SEAT_WHERE` đã cảnh báo,
 * và chính task này seed nó trong `@UC0055 AC5`.
 *
 * ⇒ Luật ở lại SERVER, client chỉ hỏi. Không có bản chép vị-từ nào ở client để mà trôi lệch — cùng
 * lẽ đã ghi cho [MEMBER_LIST_STATUS_SEATS] ngay trên.
 *
 * 🔴 Gác y hệt `join_pending`: chỉ O/A/T đọc được (member thường ⇒ 403). Hàng đợi là danh sách QUẢN TRỊ.
 */
export const MEMBER_LIST_STATUS_QUEUEING = "queueing" as const;

export const memberListStatusFilterSchema = z.enum([
  MEMBER_LIST_STATUS_QUEUEING,
  GROUP_MEMBER_STATUS.ACTIVE,
  GROUP_MEMBER_STATUS.UNCLAIMED,
  MEMBER_LIST_STATUS_SEATS,
  /**
   * 🔴 HÀNG ĐỢI: người CHƯA được công nhận là thành viên. Vô hình với tiền/quorum/quyền xem.
   *
   * Mở đúng MỘT giá trị này, và có lý do cụ thể: không nhìn thấy hàng đợi thì O/A/T không add ai
   * vào lại được — danh sách tồn tại để được xử lý. Doc ngay trên vẫn đúng cho
   * `removed`/`left`/`claimed`/`invited`: chúng là quá khứ, không phải việc đang chờ làm.
   *
   * 🔴 US-E4.F2-0597 THU HẸP NGHĨA. Câu cũ ở đây mô tả `join_pending` là *"người đã ở trong nhóm
   * nhưng chưa được công nhận (**vừa bị gỡ, hoặc tự rời** rồi muốn quay lại)"* — đúng với QĐ PO
   * 13/08, nhưng chính vế đó là bệnh: người ĐÃ RỜI nằm lẫn trong hàng đợi XIN VÀO, nên O/A duyệt
   * nhầm là `autoEnroll` sinh nghĩa vụ tiền cho người đã đi khỏi nhóm.
   *
   * Nay hai đường rời mang `left`/`removed` riêng, và `join_pending` chỉ còn nghĩa *"đang xin vào"*.
   *
   * ⚠️ Giá trị này VẪN trả cả **dữ liệu cũ** (rời trong 13/08–31/08, KHÔNG backfill — QĐ-7/C):
   * `join_pending` + `leftAt` khác NULL. Muốn đúng tập "đang xếp hàng" thì hỏi
   * {@link MEMBER_LIST_STATUS_QUEUEING} — vị-từ BA CỘT, loại luôn cả dữ liệu cũ đó.
   *
   * 🔴 Kèm điều kiện BẮT BUỘC ở tầng service: chỉ O/A/T đọc được giá trị này (member thường ⇒ 403).
   * Hàng đợi là danh sách QUẢN TRỊ; để member thường liệt kê được là phơi ra ai vừa bị gỡ khỏi nhóm.
   */
  GROUP_MEMBER_STATUS.JOIN_PENDING,
]);
export type MemberListStatusFilter = z.infer<typeof memberListStatusFilterSchema>;

/**
 * Thông điệp lỗi ký tự tên người gõ tay (biệt danh · tên suất) — một nguồn api ↔ mobile (client map theo `code`+`params`, không theo câu).
 * Khai Ở ĐÂY (trước `createGroupMemberSeatSchema`) vì hằng `const` phải khởi tạo trước schema dùng nó.
 */
export const GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE =
  "Tên hiển thị chỉ gồm chữ, số, khoảng trắng và các dấu ' - ( )";

/**
 * US-E4.F2-0396-S02 (@UC0027) — `POST /v1/groups/:id/members`: admin tạo SUẤT ĐẠI DIỆN hàng loạt.
 *
 * "Suất đại diện" = `GroupMember` KHÔNG gắn user nào (`primaryUserId` null, `status=unclaimed`) —
 * vd học sinh trong nhóm phụ huynh. Phụ huynh gắn vào sau, qua `/members/:memberId/accounts` hoặc
 * One-Time Identity Selection.
 *
 * 🔴 CHỈ nhóm `parent`. Nhóm `standard` là 1-1 (một user = một suất) nên "suất không có người" ở đó
 * là một chỗ ngồi vĩnh viễn trống — sinh ra nó chỉ tạo rác và làm sĩ số nói dối.
 */
export const createGroupMemberSeatSchema = z
  .object({
    /**
     * Tên hiển thị của suất (tên học sinh). BẮT BUỘC: suất không tên thì admin không phân biệt nổi.
     *
     * 🔴 NAME-INPUT-PATTERN-SOCIAL (review #1158 M1, @UC0027 @UC0194): tên suất là tên người GÕ TAY — cùng chuẩn hoá + chốt ký tự với biệt
     * danh (`groupDisplayNameInputSchema`): "Phong. Thủ quỹ: Phong" ⇒ 400 `{ field: "members.0.displayName", reason: "format" }`, 0 ghi.
     * Cửa chat AI `onb-add-members.binding.ts` parse CHÍNH schema này ⇒ một chốt cho cả hai cửa. Trần 120 giữ (tên suất kèm lớp dài hơn).
     */
    displayName: z.preprocess(
      normalizePersonNameInput,
      z
        .string()
        .trim()
        .min(1)
        .max(120)
        .refine((name) => !hasDisallowedPersonNameChars(name), GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE),
    ),
    /** Ngày sinh học sinh — tuỳ chọn, `YYYY-MM-DD` (ngày trần, KHÔNG mốc thời gian). */
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();

export const createGroupMemberSeatsRequestSchema = z
  .object({
    // Trần 100/lần: nhập cả lớp là ~50, nhân đôi cho thoải mái. Không mở vô hạn — một request
    // nghìn dòng là một transaction dài khoá bảng member của cả nhóm.
    members: z.array(createGroupMemberSeatSchema).min(1).max(100),
  })
  .strict();
export type CreateGroupMemberSeatsRequest = z.infer<typeof createGroupMemberSeatsRequestSchema>;

export const createGroupMemberSeatsResponseSchema = z
  .object({
    data: z.array(
      z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        status: z.literal(GROUP_MEMBER_STATUS.UNCLAIMED),
      }),
    ),
  })
  .strict();
export type CreateGroupMemberSeatsResponse = z.infer<typeof createGroupMemberSeatsResponseSchema>;

/**
 * US-E4.F2-0396-S03 (@UC0027) — `POST /v1/groups/:id/members/add`: công nhận người ở HÀNG ĐỢI
 * thành thành viên thật.
 *
 * Hai nhánh loại trừ nhau (`.strict()` + refine ⇒ gửi cả hai hoặc không gửi gì đều 400):
 *  · `userIds`     — nhóm **standard** 1-1: mỗi user nhận suất của chính mình (`linkRole=self`).
 *  · `assignments` — nhóm **parent**: gắn user vào một suất đại diện CÓ SẴN (`linkRole` mặc định
 *    `primary_contact` — người liên hệ chính của học sinh đó).
 *
 * 🔴🔴 US-E4.F2-0594 (@UC0027 · QĐ người dùng 30/08/2026) — NHÁNH THỨ BA `newUserIds`, VÀ NÓ ĐẢO
 * MỘT BẤT BIẾN. Câu ngay trên đây từng viết: *"Chỉ add được người ĐANG Ở HÀNG ĐỢI của chính nhóm
 * này. Không có đường 'add thẳng user bất kỳ': vào nhóm phải qua lời mời hoặc yêu cầu tham gia —
 * nếu không, một admin có thể kéo người lạ vào nhóm rồi gán nghĩa vụ tiền cho họ."*
 *
 * Nay O/A **thêm được người chưa từng ở nhóm**, tìm toàn hệ thống, và người đó vào **thẳng
 * `active`, không cần họ đồng ý**. Hệ quả mà câu cũ cảnh báo — *gán nghĩa vụ tiền cho người lạ* —
 * là **rủi ro CHẤP NHẬN CÓ CHỦ ĐÍCH**, đổi lại bằng ba vế bù, không phải một:
 *   1. tin `group.member.added_by_admin` báo cho **chính người bị thêm** (QĐ 31/08: tin này TẮT
 *      push được như mọi tin khác; `in_app` never-off nên nó vẫn vào hộp thư);
 *   2. audit action RIÊNG `group.member.add_by_admin` — lọc ra được đúng câu hỏi *"ai kéo tôi vào đây"*;
 *   3. quyền tự rời nhóm giữ nguyên (`FinancialPositionService` vẫn chặn khi còn nợ — KHÔNG nới).
 *
 * 🔴 VÌ SAO NHÁNH THỨ BA CHỨ KHÔNG NỚI THẲNG `userIds`: endpoint này đang xếp `ai-covered` trong
 * `docs/ai-coverage-map.json`. Nới `userIds` là **AI lập tức thừa hưởng** quyền thêm người lạ vào
 * nhóm — một quyền chưa ai quyết cho AI. Nhánh riêng thì chốt chặn AI đặt được ở đúng một chỗ.
 *
 * XOR **BA chiều**: gửi 0 nhánh, 2 nhánh hay 3 nhánh đều 400 ở biên (0 row chạm DB).
 */
/**
 * 🔴 US-E4.F2-0580 (@UC0027 @UC0028) — body cho DUYỆT / TỪ CHỐI người đang chờ vào nhóm.
 *
 * `memberIds` là id **SUẤT** (`group_members.id`), KHÔNG phải userId. Bắt buộc như vậy vì suất nhóm
 * parent đang chờ duyệt có `primaryUserId = NULL` — việc gắn người lớn vào suất (claim) chính là
 * thứ đang được duyệt, nên không thể lấy người làm khoá.
 *
 * `.strict()`: KHÔNG nhận `groupId` trong body — server lấy từ path mà guard đã resolve. Nhận
 * groupId từ body là mời một admin nhóm A thao tác lên nhóm B.
 */
export const approvePendingMembersRequestSchema = z
  .object({
    memberIds: z.array(z.string().uuid()).min(1).max(100),
  })
  .strict();

export type ApprovePendingMembersRequest = z.infer<
  typeof approvePendingMembersRequestSchema
>;

/**
 * 🔴🔴 US-E4.F2-0594 (review PR #674 HIGH-B) — TRẦN SỐ NGƯỜI cho nhánh `newUserIds` MỖI LƯỢT.
 *
 * ═══ VÌ SAO NHÁNH NÀY CẦN TRẦN RIÊNG, THẤP HƠN BA NHÁNH ANH EM ═══
 * `userIds` · `memberIds` · `assignments` · `members` (createSeats) đều cap **100**, và trần đó
 * đúng cho chúng: mỗi phần tử là một người **đã có quan hệ với nhóm** (đã xin vào, đã được mời, đã
 * có suất). `newUserIds` là phần tử DUY NHẤT của họ hàng đó mà mỗi phần tử là **một người LẠ** —
 * và mỗi người lạ nhận **một thông báo KHÔNG TẮT ĐƯỢC** (`NEVER_OFF_EVENTS`, QĐ-003: đó là vế bù
 * duy nhất của việc bỏ bước đồng ý, nên cố ý không cho tắt).
 *
 * Hai tính chất đó nhân với nhau ra một bộ khuếch đại: `@Throttle` trên route đếm **request**
 * (10/phút), còn thứ đáng đếm là **người nhận** ⇒ `100 × 10 = 1.000` thông báo ép-bật / phút / IP,
 * nội dung do người gửi tự đặt (`{groupName}` + `{actorName}`). Vế bù sinh ra để BẢO VỆ người bị
 * thêm trở thành kênh để làm phiền họ.
 *
 * ═══ VÌ SAO ĐÚNG CON SỐ NÀY, KHÔNG PHẢI MỘT SỐ NGHE HỢP LÝ ═══
 * Lấy theo mặt bằng ĐANG CÓ trong chính file này, không bịa:
 *  · `GROUP_MEMBERS_MAX_LIMIT = 50` · `MY_GROUPS_MAX_LIMIT = 50` · `MEMBER_CONTRIBUTIONS_MAX_LIMIT = 50`
 *    — đơn vị "một lô người mà một con người thao tác trong một lượt";
 *  · `IMPORT_ROWS_MAX = 200` (`member-import.ts`) — CAO hơn vì import CSV chỉ dựng **bản nháp**:
 *    0 tư cách thành viên, 0 nghĩa vụ tiền, 0 thông báo. Không so được;
 *  · `GLOBAL_USER_SEARCH_MAX_LIMIT = 20` (`users-search.ts`) — nguồn nạp danh sách này, nhưng là
 *    trang tìm kiếm; người dùng tick qua nhiều trang nên lấy 20 là chặn oan.
 * ⇒ **50**: khớp mặt bằng "một lô người" của cùng file, và phủ được ca dùng thật lớn nhất đã biết
 * (một lớp học ~40-50 phụ huynh) trong ĐÚNG MỘT lượt bấm.
 *
 * ⚠️ ĐÂY KHÔNG PHẢI CHỐT HOÀN CHỈNH — nói thẳng để không ai đọc nhầm là đã xong. Nó chỉ **giảm một
 * nửa** trần xấu nhất (1.000 → 500 tin/phút/IP). Chốt đúng là throttle theo **NGƯỜI NHẬN**
 * (dedupe/cooldown theo cặp `(groupId, addedUserId)`), nhưng thứ đó cần hạ tầng đếm chia sẻ
 * (Redis) ⇒ là một quyết định hạ tầng, không phải một dòng contract. Nợ đã ghi trong PR #674.
 */
export const ADD_GLOBAL_MEMBERS_MAX = 50;

export const addGroupMembersRequestSchema = z
  .object({
    userIds: z.array(z.string().uuid()).min(1).max(100).optional(),
    /**
     * US-E4.F2-0594 (@UC0027) — người **chưa từng ở nhóm**, tìm toàn hệ thống, vào thẳng `active`.
     *
     * Tên `newUserIds` cố ý KHÔNG phải `anyUserIds`: server vẫn **rẽ về nhánh cũ** (tái kích hoạt
     * suất sẵn có) khi người đó từng ở nhóm — nếu không sẽ va partial unique
     * `one_active_seat_per_user_per_group` ⇒ 500. Chữ "new" nói về Ý ĐỊNH của người bấm ("người
     * này mới với nhóm"), không phải một khẳng định về dữ liệu.
     *
     * 🔴 Trần RIÊNG `ADD_GLOBAL_MEMBERS_MAX`, THẤP HƠN 100 của ba nhánh anh em — xem giải trình ở
     * hằng đó (review PR #674 HIGH-B).
     */
    newUserIds: z.array(z.string().uuid()).min(1).max(ADD_GLOBAL_MEMBERS_MAX).optional(),
    assignments: z
      .array(
        z
          .object({
            userId: z.string().uuid(),
            groupMemberId: z.string().uuid(),
            linkRole: z.enum(["primary_contact", "secondary_contact"]).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(100)
      .optional(),
  })
  .strict()
  /**
   * 🔴 ĐẾM rồi so `=== 1`, KHÔNG chuỗi `!==` như bản XOR 2 chiều.
   *
   * `Boolean(a) !== Boolean(b) !== Boolean(c)` trong JS là **phép so trái-sang-phải trên boolean**,
   * không phải "đúng một trong ba": `(true !== true) !== false` ⇒ `false !== false` ⇒ `true` —
   * tức gửi CẢ BA nhánh sẽ **lọt**. Đây là ca đã có test riêng ở `groups.test.ts` chính vì cái bẫy
   * đó trông vô hại khi mở rộng từ hai lên ba.
   */
  /**
   * 🔴 Không cho TRÙNG id trong `newUserIds` (review PR #674 LOW).
   *
   * Gửi cùng một uuid hai lần thì lượt hai rơi vào chốt `stillMember` ⇒ **409 cho CẢ LÔ** + rollback
   * toàn bộ. Fail-closed nên không phải lỗ hổng, nhưng nó là một 409 khó hiểu cho một thao tác hợp
   * lệ (client dựng danh sách từ nhiều nguồn — quét QR rồi tìm tên, chẳng hạn). Chặn ở BIÊN thì lỗi
   * nói đúng chuyện đang xảy ra, và 0 row chạm DB.
   */
  .refine((v) => v.newUserIds === undefined || new Set(v.newUserIds).size === v.newUserIds.length, {
    message: "`newUserIds` có id trùng lặp — mỗi người chỉ gửi một lần.",
    path: ["newUserIds"],
  })
  .refine(
    (v) => [v.userIds, v.newUserIds, v.assignments].filter(Boolean).length === 1,
    {
      message:
        "Gửi ĐÚNG MỘT trong ba: `userIds` (công nhận người ở hàng đợi) · `newUserIds` (thêm người " +
        "chưa từng ở nhóm) · `assignments` (gắn người vào suất đại diện, nhóm parent).",
    },
  );
export type AddGroupMembersRequest = z.infer<typeof addGroupMembersRequestSchema>;

export const addGroupMembersResponseSchema = z
  .object({
    /** Số suất chuyển sang `active`. Client dùng để hiện toast "Đã thêm N thành viên". */
    added: z.number().int().min(0),
  })
  .strict();
export type AddGroupMembersResponse = z.infer<typeof addGroupMembersResponseSchema>;

/**
 * Shape response cho POST /v1/groups.
 * Trả group mới + role của caller (owner) để mobile điều hướng Group Profile (story 0020).
 * KHÔNG lộ danh sách member/secret — chỉ field tối thiểu cho điều hướng (C2/C3).
 */
export const groupResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  // Role của người gọi trong nhóm vừa tạo — luôn "owner" cho slice 0018.
  role: groupRoleSchema,
  createdAt: z.string().datetime(),
});

export type GroupResponse = z.infer<typeof groupResponseSchema>;

/* ===========================================================================
 * GET /v1/users/me/groups — View My Group List (US-E4.F2-0019 / @UC0015).
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 3: endpoint list-my-groups KHÔNG có sẵn trong danh sách
 * Phase 3 (plan chỉ POST/PATCH /groups, members/role, transfer, invitations/join). Đây là read
 * list hợp lý thuộc scope Groups → bổ sung khi build Phase 3 (xem index §6 + SD-2).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII (C2): chỉ nhóm caller là member ACTIVE (server lọc theo token, chống IDOR) — item KHÔNG
 *    mang field cross-user/secret. `.strict()` chặn field lạ lọt ra response.
 *  - ⚠️ KHÔNG có field SỐ DƯ/tiền: số dư quỹ phụ thuộc Ledger Phase 6 (chưa build) → CẮT phạm vi,
 *    KHÔNG bịa giá trị tài chính (guardrail A4/B1). Tách story khi Ledger xong.
 *  - Cursor opaque string — đối xứng common/pagination/cursor (envelope { data, pageInfo }).
 * ========================================================================= */

/** Max page-size cho list my-groups. Default (20) do backend áp khi vắng. */
export const MY_GROUPS_MAX_LIMIT = 50;

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS). */
export const MY_GROUPS_MAX_QUERY_LENGTH = 512;

/**
 * Một item trong danh sách "Nhóm của tôi" (AC1, KHÔNG số dư):
 *  - id/name: định danh + tên nhóm (tap → Group Profile, AC3).
 *  - avatarUrl: ảnh nhóm; null khi nhóm chưa đặt avatar.
 *  - memberCount: số thành viên ACTIVE của nhóm (≥1, vì caller cũng là member active).
 *  - role: vai trò của CHÍNH caller trong nhóm (owner|admin|treasurer|member) — cho role badge.
 *  - status: trạng thái membership của caller — luôn 'active' ở list này (server đã lọc), giữ field
 *    cho status badge (AC1) + tường minh contract.
 *  - needsAttention: cờ "việc cần xử lý" tối giản (AC2 — warning badge). Nội dung THẬT phụ thuộc
 *    Collections P7 / Voting P8 (chưa build) → hiện luôn false (KHÔNG bịa logic nghiệp vụ).
 * `.strict()` chặn rò field thừa (vd balance/userId/secret) ra response.
 */
/**
 * 🔴 US-E23.F1-0331-S01 (@UC9510) — placeholder khi ẩn tiền ở TẦNG SERVER.
 *
 * §3.8: *"Redaction ẩn ở TẦNG SERVER = `'•••'`, KHÔNG `0`"* — `0` là **nói dối về tiền**: quỹ có 5
 * triệu mà hiện `0` thì thành viên tưởng quỹ rỗng và mất niềm tin, đúng thứ sản phẩm này tồn tại để
 * xây. Số bị ẩn **không rời API**.
 *
 * Mirror `PUBLIC_STATEMENT_REDACTED` (compliance.ts) — CÙNG placeholder, KHÔNG đẻ hằng thứ hai.
 */
export const GROUP_MONEY_REDACTED = "•••" as const;

/** Tiền trên wire = STRING minor (JSON không có int64 an toàn). Không float, không number. */
const GROUP_MONEY_INTEGER_STRING = z.string().regex(/^-?(0|[1-9][0-9]*)$/);

/** Field không-tiền của một item "Nhóm của tôi" — phần dùng chung cho cả hai nhánh redaction. */
const myGroupListItemBase = {
  id: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  memberCount: z.number().int().min(0),
  role: groupRoleSchema,
  status: groupMemberStatusSchema,
  needsAttention: z.boolean(),
  /**
   * US-E23.F1-0331 (@UC9511): Figma hiện trạng thái *"Lưu trữ"* ở 4 chỗ mà repo có **0 endpoint**.
   * Đây là vế đọc; vế ghi ở S06 (`/archive`, `/unarchive`) — **thay cho xoá cứng**, vì §3.8
   * *"GDPR = ẨN DANH, KHÔNG XOÁ SỔ"* và các bảng append-only có trigger DB cấm DELETE (docs/generated/facts.md).
   */
  isArchived: z.boolean(),
};

/**
 * 🔴 DISCRIMINATED UNION theo `redacted`, KHÔNG phải field optional.
 *
 * Nhánh ẩn mang literal `'•••'` ⇒ **trạng thái sai không biểu diễn được**: không thể vừa
 * `redacted: true` vừa mang số thật, và không thể lỡ trả `0` khi ý là "ẩn". Nếu dùng
 * `balanceMinor?: string` thì cả hai lỗi đó đều **hợp lệ về kiểu** và chỉ lộ ra ở mắt người dùng.
 */
export const myGroupListItemSchema = z.discriminatedUnion("redacted", [
  z.object({ ...myGroupListItemBase, redacted: z.literal(false), balanceMinor: GROUP_MONEY_INTEGER_STRING, currency: z.string().length(3) }).strict(),
  z.object({ ...myGroupListItemBase, redacted: z.literal(true), balanceMinor: z.literal(GROUP_MONEY_REDACTED), currency: z.string().length(3) }).strict(),
]);

export type MyGroupListItem = z.infer<typeof myGroupListItemSchema>;

/** Query list: cursor opaque optional, limit ∈ [1,50] optional (default 20 backend áp). */
export const myGroupsListQuerySchema = z.object({
  cursor: z.string().max(MY_GROUPS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(MY_GROUPS_MAX_LIMIT).optional(),
});

export type MyGroupsListQuery = z.infer<typeof myGroupsListQuerySchema>;

/**
 * Envelope list khớp quy ước repo `{ data, pageInfo }`. KHÔNG có field tổng số dư (Phase 6 deferred).
 * data sắp xếp activity gần nhất lên đầu (group.updatedAt desc — AC5).
 */
export const myGroupsListResponseSchema = z.object({
  data: z.array(myGroupListItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type MyGroupsListResponse = z.infer<typeof myGroupsListResponseSchema>;

/* ===========================================================================
 * GET /v1/groups/:id — View Group Profile (US-E4.F3-0020 / @UC0055).
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 3: `GET /v1/groups/:id` là read-detail bổ trợ cho Phase 3
 * Groups (plan liệt kê POST/PATCH /groups + members/role; read-detail là complement hợp lý,
 * thêm theo SD-2 giống list-my-groups). Member-only qua GroupRoleGuard (tenant-isolation 404
 * đồng nhất cho non-member/cross-tenant — chống IDOR, đã chốt ở 0018).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII / tenant isolation (C2/C4): chỉ member ACTIVE của nhóm xem được (server enforce qua guard).
 *  - ⚠️ KHÔNG có field SỐ DƯ/tiền quỹ: số dư phụ thuộc Ledger Phase 6 (chưa build) — CẮT phạm vi,
 *    KHÔNG bịa giá trị tài chính (guardrail A4/B1). Fund item chỉ id/name + cờ needsConfig.
 *  - Role của caller trả về để client gate action (AC2) — backend KHÔNG dựng action button.
 *  - `.strict()` chặn rò field thừa (vd balance/bankAccount/secret) ra response.
 * ========================================================================= */

/**
 * Một quỹ trong Group Profile (AC1/AC4 — KHÔNG số dư):
 *  - id/name: định danh + tên quỹ (tap → Fund deep-screen thuộc story 0021).
 *  - needsConfig: cờ "chưa cấu hình tài khoản ngân hàng" (Fund.bankAccount null) — client render
 *    badge "Chưa cấu hình" (AC4). KHÔNG lộ nội dung bankAccount (chỉ cờ boolean).
 * `.strict()` chặn rò field tài chính/secret (balance/bankAccount) ra response.
 */
export const groupProfileFundItemSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    needsConfig: z.boolean(),
  })
  .strict();

export type GroupProfileFundItem = z.infer<typeof groupProfileFundItemSchema>;

/**
 * Cờ action role-gated (AC2) — canManageMembers/canEditSettings/canCreateFund = true CHỈ khi
 * role owner/admin. Server tính (single source of truth), client KHÔNG tự nâng quyền (C4).
 *
 * 🔴 US-E24.F31-0436 — HAI MỨC QUYỀN trên cùng một màn, đừng gộp:
 *  - `canManageMembers` = owner **HOẶC** admin. Khớp `DELETE :id/members/:memberId` (owner+admin).
 *  - `canChangeRole` / `canTransferOwnership` = **CHỈ owner**. Khớp
 *    `@RequireGroupRoles(GROUP_ROLE.OWNER)` ở `groups.controller.ts` cho
 *    `POST :id/members/:mid/role` và `POST :id/ownership/transfer`.
 *
 * Trước 17/08/2026 chỉ có cờ mức trên, nên màn hình muốn ẩn hai nút owner-only phải **tự suy** từ
 * `role` — đúng thứ docblock của kiểu này cấm. Thiếu cờ không gây lỗi tiền lẫn lỗi quyền (server
 * vẫn 403), nhưng nó dẫn quản trị viên vào một nút chắc chắn hỏng.
 */
export const groupProfileActionsSchema = z
  .object({
    canManageMembers: z.boolean(),
    canEditSettings: z.boolean(),
    canCreateFund: z.boolean(),
    /** CHỈ owner — xem docblock trên. */
    canChangeRole: z.boolean(),
    /** CHỈ owner. Hành động **không đảo được**, không có bước người kia chấp nhận. */
    canTransferOwnership: z.boolean(),
    /**
     * 🔴 US-MOB.F3-0741 (@UC0026 · QĐ người dùng 08/09/2026) — CHỈ owner. Cửa "Vùng nguy hiểm":
     * lưu trữ nhóm + xoá nhóm.
     *
     * Khớp `@RequireGroupRoles(GROUP_ROLE.OWNER)` ở `groups.controller.ts` cho CẢ HAI route
     * (`POST :id/archive` và `DELETE :id`) — *"lưu trữ nhóm là quyết định cuối vòng đời, không phải
     * việc quản trị thường ngày"*.
     *
     * 🔴 VÌ SAO MỘT CỜ CHO HAI HÀNH ĐỘNG: chúng luôn cùng vai. Hai cờ luôn-bằng-nhau là lời khẳng
     * định sai rằng chúng độc lập, và người sau sẽ gác một cái mà quên cái kia.
     *
     * 🔴 VÌ SAO KHÔNG DÙNG LẠI CỜ CÓ SẴN: `canEditSettings` là owner **HOẶC admin** ⇒ quản trị viên
     * sẽ thấy vùng nguy hiểm rồi ăn 403 ở cả hai nút. `canTransferOwnership` tình cờ cùng vai
     * (owner-only) nhưng trả lời câu KHÁC — mượn nó là đúng cái bẫy "cờ này không phải cờ kia" đã
     * trả giá ở review PR #646 (`canManageMembers` vs `canInviteMembers`).
     */
    canManageGroupLifecycle: z.boolean(),
    /**
     * 🔴 US-MOB.F12-0760 (@UC9514 · QĐ người dùng 10/09/2026) — CỬA XUẤT DỮ LIỆU: owner **HOẶC**
     * admin **HOẶC** thủ quỹ.
     *
     * Khớp `@RequireGroupRoles(OWNER, ADMIN, TREASURER)` ở `reports.controller.ts:140` cho
     * `POST :id/reports/export` (và hai route đọc kèm theo).
     *
     * 🔴 VÌ SAO PHẢI LÀ CỜ MỚI, KHÔNG MƯỢN `canEditSettings`: cờ đó là `GROUP_MANAGE_ROLES` =
     * owner **hoặc admin**, KHÔNG có thủ quỹ. Mượn nó nghĩa là **giấu mục xuất dữ liệu khỏi đúng
     * người có nghề giữ sổ** — trong khi server cho phép họ. Đây là lần thứ ba repo gặp cùng cái
     * bẫy "cờ này không phải cờ kia" (`canManageMembers` vs `canInviteMembers` ở review PR #646;
     * `canEditSettings` vs `canManageGroupLifecycle` ở 0741) — nên viết ra một cờ riêng, đúng vai.
     *
     * ⚠️ Thủ quỹ hiện KHÔNG gán được qua UI (QĐ 25/08, MVP tạm ẩn vai), nên trên dữ liệu hôm nay cờ
     * này gần như trùng `canEditSettings`. Đó KHÔNG phải lý do để mượn cờ kia: vai `treasurer` vẫn
     * tồn tại trong DB, người đang giữ vai vẫn giữ, và ngày bật lại vai thì không ai nhớ quay lại
     * sửa chỗ này.
     */
    canExportData: z.boolean(),
    /**
     * 🔴 US-E23.F1-0867 (@UC0048 · QĐ PO 30/09/2026 — *"ai cũng xuất được sổ thu chi ⇒ minh bạch"*) — mục "Xuất báo cáo thu chi"
     * (PDF `finance`) + "Bản đã xuất". MỌI vai — nội dung tệp của member thường lọc theo tầm nhìn của CHÍNH họ trong app.
     *
     * 🔴 Cờ RIÊNG, KHÔNG nới `canExportData` (lần thứ năm của bẫy "cờ này không phải cờ kia"): cờ kia vẫn nghĩa là "xuất DỮ LIỆU"
     * — CSV + hai PDF tách cũ, O/A/T. Nới nó thì một client gác CSV bằng `canExportData` phơi nút cho member rồi 403.
     * Dẫn xuất từ `MEMBER_EXPORTABLE_GROUP_REPORT_KINDS` (cùng tập mà service gác) — không phải hằng `true` viết tay.
     */
    canExportFinanceReport: z.boolean(),
    /**
     * 🔴 US-AIV2-0112 H11 (@UC0024 · QĐ PO Q2 28/09) — CHỈ owner. Màn "Sổ công khai" (tạo · xem danh sách · gỡ link xem sổ
     * không đăng nhập). Khớp `@RequireGroupRoles(GROUP_ROLE.OWNER)` ở `transparency.controller.ts` cho CẢ BA cửa
     * `POST|GET|DELETE /groups/:id/transparency-links` — đọc không rộng hơn ghi.
     *
     * Cờ RIÊNG, không mượn `canManageGroupLifecycle`/`canTransferOwnership`: cùng vai hôm nay nhưng trả lời câu khác (lần thứ tư
     * của bẫy "cờ này không phải cờ kia" — xem `canExportData`). Ngày nới cửa link cho admin thì chỉ sửa MỘT chỗ.
     */
    canManagePublicLink: z.boolean(),
  })
  .strict();

export type GroupProfileActions = z.infer<typeof groupProfileActionsSchema>;

/**
 * Shape response cho GET /v1/groups/:id (group shell + role-gated flags + KHỐI TÀI CHÍNH):
 *  - id/name/purpose(mô tả)/avatarUrl: header nhóm. purpose/avatarUrl null khi chưa đặt.
 *  - memberCount: số GroupMember ACTIVE của nhóm (≥1, vì caller cũng là member active).
 *  - role: vai trò của CHÍNH caller (owner|admin|treasurer|member) — client gate action (AC2).
 *  - funds: danh sách quỹ chưa archive (id/name/needsConfig) — KHÔNG số dư từng quỹ (ngoài phạm vi).
 *  - actions: cờ role-gated (AC2). Server tính từ role; client KHÔNG tự nâng quyền.
 *
 * US-E4.F3-0389 (@UC0055): thêm khối tài chính cấp group (comment cũ "KHÔNG số dư — Phase 6
 * deferred" đã lỗi thời từ khi `sumGroupCashBalances` tồn tại):
 *  - `balanceMinor`: Σ CASH các quỹ active.
 *  - `totalIncomeMinor`: tổng THU lũy kế (Σ account INCOME, ĐÃ đảo dấu về dương — cache lưu
 *    debit-dương nên INCOME credit-normal nằm âm trong DB).
 *  - `totalExpenseMinor`: tổng CHI lũy kế (Σ account EXPENSE, debit-normal — giữ nguyên).
 *  - DISCRIMINATED UNION theo `redacted` (mirror `myGroupListItemSchema`): nhánh ẩn mang literal
 *    '•••' cho CẢ BA số — trạng thái "vừa ẩn vừa mang số thật" không biểu diễn được. Luật: O/A/T
 *    luôn thấy; member thường cần MỌI quỹ `transparency_mode=public` (tổng là số cộng gộp — ẩn
 *    một phần trong con số cộng gộp là không ẩn được).
 * `.strict()` chặn rò field thừa (bankAccount/secret) ra response (C2).
 */
const groupProfileBase = {
  id: z.string().uuid(),
  name: z.string(),
  purpose: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  memberCount: z.number().int().min(0),
  role: groupRoleSchema,
  funds: z.array(groupProfileFundItemSchema),
  actions: groupProfileActionsSchema,
  currency: z.string().length(3),
  /**
   * US-E4.F1-0395 (@UC0055) — loại nhóm dạng OBJECT kèm NHÃN SONG NGỮ. `null` khi chưa phân loại.
   *
   * 🔴 ĐẢO quyết định 0388 ("trả CODE, nhãn lấy từ `GET /v1/group-categories`"), có lý do:
   * bắt client tải cả danh mục rồi TỰ TRA mã→nhãn là bắt nó dựng lại một sự thật server đã biết —
   * FK `categoryRef` nằm ngay trong query hồ sơ, `select` qua quan hệ là CÙNG một round-trip.
   * Tệ hơn: hai request phải cùng thành công để vẽ đúng MỘT cái nhãn, nên danh mục lỗi ⇒ chip biến
   * mất dù nhóm có đặt loại hẳn hoi ⇒ người dùng thấy nhóm mình "không có loại".
   * Lý do cũ ("một nguồn nhãn duy nhất") vẫn được giữ: nguồn vẫn là bảng `group_categories`, chỉ
   * khác là SERVER tra hộ thay vì client tra.
   *
   * 🔴 CÓ `type` ở đây — **ĐẢO quyết định cũ, QĐ người dùng 14/08/2026.**
   *
   * Câu cũ ở chỗ này là *"KHÔNG có `type`, cố ý — lộ ra biên end-user là mời viết
   * `if (type == 'parent')` ở client"*. Nỗi lo đó đúng về nguyên tắc nhưng đã bị một ràng buộc
   * THẬT vượt qua: `POST /v1/groups/:id/members` (tạo suất đại diện) ném **422
   * `GROUP_SEATS_PARENT_ONLY`** cho nhóm `standard` (`members.service.ts` đọc `groups.type`). Client
   * phải ẩn HẲN lối đó ở nhóm standard — để người dùng gõ xong cả danh sách học sinh rồi mới ăn 422
   * là thiết kế tồi hơn nhiều so với một nhánh `if` ở client.
   *
   * Vì sao KHÔNG tra qua `GET /v1/group-categories` cho đỡ đổi contract: `parent` không phải MỘT
   * code mà là **ba** (`parent_class`, `family_couple`, `family_travel`), và danh mục **mở** — admin
   * thêm được lúc chạy qua `POST /admin/dictionaries/group-categories`. Danh sách code chép tay ở
   * client sẽ mục đúng như enum `GroupCategory` 6-giá-trị cũ đã mục (4 code mới rơi vào `null` im
   * lặng). Còn tra qua catalog thì bắt màn chi tiết nhóm gọi thêm một request nữa — chính là thứ
   * đoạn doc ngay trên vừa bác bỏ khi đảo QĐ 0388.
   *
   * ⇒ Server tra hộ, một round-trip, một nguồn sự thật (`group_categories.type`).
   *
   * `GET /v1/group-categories` GIỮ NGUYÊN: màn *tạo nhóm* cần liệt kê loại để người dùng CHỌN —
   * bài toán khác với *hiển thị* loại của một nhóm đã có.
   */
  category: groupCategoryEmbeddedSchema.nullable(),
};

export const groupProfileResponseSchema = z.discriminatedUnion("redacted", [
  z
    .object({
      ...groupProfileBase,
      redacted: z.literal(false),
      balanceMinor: GROUP_MONEY_INTEGER_STRING,
      totalIncomeMinor: GROUP_MONEY_INTEGER_STRING,
      totalExpenseMinor: GROUP_MONEY_INTEGER_STRING,
    })
    .strict(),
  z
    .object({
      ...groupProfileBase,
      redacted: z.literal(true),
      balanceMinor: z.literal(GROUP_MONEY_REDACTED),
      totalIncomeMinor: z.literal(GROUP_MONEY_REDACTED),
      totalExpenseMinor: z.literal(GROUP_MONEY_REDACTED),
    })
    .strict(),
]);

export type GroupProfileResponse = z.infer<typeof groupProfileResponseSchema>;

/**
 * Role được phép thao tác quản lý (quản lý thành viên / cài đặt nhóm / tạo quỹ) trong nhóm.
 * Một nguồn sự thật cho api (tính actions) + test. AC2: CHỈ owner/admin (treasurer/member view-only).
 */
export const GROUP_MANAGE_ROLES: readonly GroupRole[] = [
  GROUP_ROLE.OWNER,
  GROUP_ROLE.ADMIN,
];

/**
 * Tính cờ action role-gated từ role caller (AC2).
 *
 * owner/admin → nhóm cờ "quản lý" true. **CHỈ owner** → hai cờ owner-only true.
 *
 * 🔴 `isOwner` cố ý so sánh TRỰC TIẾP với `GROUP_ROLE.OWNER`, không dùng một hằng danh sách như
 * `GROUP_MANAGE_ROLES`: một danh sách tên chung sẽ có ngày bị ai đó thêm `ADMIN` vào cho tiện, và
 * lúc đó hai cờ này lặng lẽ nói sai về quyền. Ở đây chỉ có đúng một vai, viết thẳng ra.
 */
export function computeGroupProfileActions(
  role: GroupRole,
): GroupProfileActions {
  const canManage = GROUP_MANAGE_ROLES.includes(role);
  const isOwner = role === GROUP_ROLE.OWNER;
  // 🔴 Tập vai RIÊNG, viết thẳng — xem docblock của cờ. KHÔNG dùng `GROUP_MANAGE_ROLES` (thiếu
  // thủ quỹ) và cũng không thêm `TREASURER` vào danh sách đó (nó đang đúng cho ba cờ khác).
  const canExportData = canManage || role === GROUP_ROLE.TREASURER;
  return {
    canManageMembers: canManage,
    canEditSettings: canManage,
    canCreateFund: canManage,
    canChangeRole: isOwner,
    canTransferOwnership: isOwner,
    canManageGroupLifecycle: isOwner,
    canExportData,
    // 🔴 US-E23.F1-0867 — O/A/T xuất được mọi kind (gồm `finance`); vai khác chỉ khi `finance` nằm trong tập của member.
    canExportFinanceReport: canExportData || MEMBER_EXPORTABLE_GROUP_REPORT_KINDS.has(GROUP_REPORT_KIND.FINANCE),
    canManagePublicLink: isOwner,
  };
}

/* ===========================================================================
 * GET /v1/groups/:id/members — View Member List (US-E4.F4-0022 / @UC0055).
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 3: `GET /v1/groups/:id/members` thuộc Groups Phase 3
 * (members.service trên module groups). Member-only qua GroupRoleGuard (tenant-isolation 404
 * đồng nhất cho non-member/cross-tenant/:id-không-UUID — chống IDOR, đã chốt ở 0018).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII / tenant isolation (C2/C4): chỉ member ACTIVE của nhóm xem danh sách (server enforce qua
 *    guard). CHỈ trả thành viên status='active' (loại invited/join_pending/removed/left — AC3).
 *  - ⚠️ KHÔNG đụng tài chính/tiền (story xem danh sách thuần). KHÔNG lộ email/phone thô (chỉ
 *    fullName/displayName/avatar cho UI). `.strict()` chặn rò field thừa ra response.
 *  - actions role-gated (canInviteMembers/canManageMembers) tính từ role caller — owner/admin true,
 *    treasurer/member false (AC1/AC2). Server là single source of truth; client KHÔNG tự nâng quyền.
 *    KHÔNG remove/invite thật ở đây (story 0023/0027) — chỉ visibility flags.
 * ========================================================================= */

/** Max page-size cho list members. Default (20) do backend áp khi vắng. */
export const GROUP_MEMBERS_MAX_LIMIT = 50;

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS). */
export const GROUP_MEMBERS_MAX_QUERY_LENGTH = 512;

/**
 * 🔴 US-E4.F4-0595 (@UC0055 @UC0027) — số hàng CHỜ DUYỆT được GHIM vào danh sách thành viên.
 *
 * Khác `managers` (0551, KHÔNG cắt): tập quản trị viên có trần tự nhiên (owner đúng 1, admin vài
 * người), còn hàng chờ thì không — một link mở đăng lên Zalo lớp có thể sinh 40 lượt xin vào trong
 * một buổi tối. Ghim cả 40 hàng vào giữa màn Thành viên là biến mục ghim thành chính cái danh sách
 * mà nó đang cố giúp người dùng khỏi phải cuộn.
 *
 * ⇒ Ghim 5 hàng đầu (chờ lâu nhất trước) + `pendingTotalCount` là **tổng THẬT**, và một nút
 * "Xem tất cả" đi sang màn `?status=join_pending`.
 *
 * ⚠️ Con số 5 là **ngưỡng trình bày**, KHÔNG phải giới hạn quyền: `pendingTotalCount` vẫn nói đúng
 * sự thật, nên không có ai bị giấu khỏi quản trị viên — chỉ bị đẩy sang màn sau.
 */
export const MEMBER_LIST_PENDING_PIN_LIMIT = 5;

/**
 * Một item trong Member List (AC1 — KHÔNG tài chính):
 *  - id: GroupMember.id (định danh membership — dùng cho remove/role action story sau).
 *  - userId: User.id (chủ membership). KHÔNG lộ email/phone.
 *  - fullName: tên thật của user; null khi chưa đặt.
 *  - displayName: nickname per-group (GroupMember.displayName, E2-25); null khi không đặt.
 *  - avatarUrl: ảnh đại diện user; null khi chưa có.
 *  - role: vai trò trong nhóm (owner|admin|treasurer|member) — cho role-badge.
 *  - joinedAt: thời điểm tham gia (ISO-8601); null nếu thiếu (dữ liệu cũ).
 * `.strict()` chặn rò field thừa (email/phone/passwordHash/secret) ra response (C2).
 */
/**
 * @UC0055 — NGÀY SINH của suất: ngày LỊCH trần `YYYY-MM-DD` CÓ THẬT (2018-02-30 ⇒ fail), không giờ, không múi giờ. MỘT schema cho
 * chiều ĐỌC (`memberListItemBase.birthDate`) lẫn chiều GHI (`updateMemberBirthDateRequestSchema`) — hai đầu của cùng một cột không được
 * lệch định dạng. Cùng dạng dây với `newMember.birthDate`.
 */
const memberBirthDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((s) => {
    const d = new Date(`${s}T00:00:00.000Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  });

const memberListItemBase = {
  id: z.string().uuid(),
  // 🔴 US-E24.F3-0352-S03 (@UC9574): NULLABLE. Suất `unclaimed` (chỗ ngồi chưa có người đại diện)
  // KHÔNG có `primaryUserId` — trước đây schema đòi uuid nên chỉ cần lọt một suất như vậy vào response
  // là `.strict()` nổ 500. Nới NULL là điều kiện cần để `?status=unclaimed` biểu diễn được sự thật
  // "suất này chưa có ai đứng tên"; `0`/chuỗi rỗng đều là bịa.
  userId: z.string().uuid().nullable(),
  fullName: z.string().nullable(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: groupRoleSchema,
  // 🔴 US-E24.F3-0352-S03 (@UC9574): CÓ cờ phân biệt `active` vs `unclaimed` ngay trong item. Không có
  // nó thì client lọc `?status=unclaimed` xong vẫn không dựng được một danh sách trộn, và "suất chưa
  // có chủ" lại vô hình lần nữa — lần này ở tầng UI.
  status: memberListStatusFilterSchema,
  joinedAt: z.string().datetime().nullable(),
  /**
   * 🔴 US-AIV2-0111 C11 (@UC0343 · QĐ PO 28/09 tối) — hàng CHỜ DUYỆT này là lời xin NHẬN một suất giữ chỗ.
   *
   * Người duyệt phải thấy *"Khánh xin nhận suất Hùng"* TRƯỚC khi bấm, vì bấm "Cho phép" ở hàng này KHÔNG thêm một người
   * mới — nó gắn người xin vào suất Hùng cùng mọi khoản phải đóng của suất đó. Thiếu dòng này thì hai hành động khác nhau
   * về tiền trông y hệt nhau trên màn hình.
   *
   * Server CHỈ đặt field khi hàng có lời xin (vắng ⇒ hàng chờ thường / thành viên thường). `displayName` = tên suất giữ chỗ
   * (O/A tự gõ lúc giữ chỗ) — không PII mới; `null` khi suất chưa đặt tên.
   */
  claimTarget: z
    .object({ memberId: z.string().uuid(), displayName: z.string().nullable() })
    .strict()
    .optional(),
  /**
   * 🔴 US-AIV2-0112 H10 (@UC0350 · QĐ 29/09) — dòng NGƯỜI LỚN (ghế `self`) của nhóm phụ huynh: cô giáo / ban đại diện / trưởng
   * nhóm không có con trong lớp. Chỉ xuất hiện ở hàng chờ duyệt và mục Quản trị (luật ẩn 0733 giữ ở danh sách chung, sĩ số,
   * bảng đóng tiền). Ghế này KHÔNG gánh nghĩa vụ (`buildObligationMemberWhere`). Vắng ⇒ suất con / nhóm thường.
   */
  adultSeat: z.literal(true).optional(),
  /**
   * 🔴 ROLE-CHANGE-BY-SEAT (@UC0019 @UC0020 · 12/10/2026) — NGƯỜI XEM đổi vai được cho SUẤT này không (`POST .../members/:mid/role`
   * với `:mid` = [id]). Server tính cho TỪNG dòng: người xem là chủ nhóm (`canChangeRole`) VÀ suất `active` VÀ có một suất GIỮ QUYỀN rõ
   * ràng — chính suất đó nếu phụ huynh đứng tên chính (`primaryUserId` khác NULL), hoặc (nhóm phụ huynh, QĐ 12/10/2026) suất phụ huynh
   * duy nhất của nó đứng tên chính (`roleHolderName` đi kèm); hai phụ huynh giữ quyền ở hai suất riêng / không có suất nào ⇒ mơ hồ ⇒
   * 409 `GROUP_ROLE_SEAT_NOT_PRIMARY` — VÀ suất giữ quyền không mang vai chủ nhóm (kể cả vai HIỂN THỊ `owner` của suất trưởng nhóm
   * đại diện — chặt hơn server) VÀ không phải suất của chính người xem.
   *
   * BẮT BUỘC (không optional): client đọc vắng = `false` (fail-closed), nên một nhánh server quên điền phải đỏ ở biên chứ không
   * lặng lẽ ẩn nút. Đây là lớp CHỐNG DẪN VÀO LỖI CHẮC CHẮN, không phải cổng quyền — cổng thật là route (owner-only + luật suất);
   * các chốt động (còn nợ quỹ, thủ quỹ phải có tài khoản) KHÔNG nằm trong cờ.
   */
  roleAssignable: z.boolean(),
  /**
   * 🔴 QĐ người dùng 12/10/2026 — TÊN PHỤ HUYNH NHẬN VAI khi đổi vai ở suất CON này (nhóm phụ huynh), để app hỏi đúng người ("Đổi vai trò của
   * <phụ huynh>?") thay vì tên bé. Vai là của MỘT phụ huynh: người đứng tên chính của suất, hoặc — suất không ai đứng tên chính ("bé thứ
   * hai") — phụ huynh duy nhất của nó đứng tên chính một suất khác (server áp vai lên suất đó; `role` của dòng là phép chiếu để hiển thị).
   * Phụ huynh còn lại của suất KHÔNG nhận vai (liên kết không nâng vai).
   *
   * CÓ MẶT CHỈ KHI `roleAssignable = true` (người xem là chủ nhóm — người đã thấy tên người đại diện ở màn chi tiết): không mở thêm đường lộ
   * tên cho thành viên thường. `null` = phụ huynh đã ẩn danh / chưa có tên. Vắng ở nhóm thường và dòng người lớn — tên dòng đã là người nhận vai.
   */
  roleHolderName: z.string().nullable().optional(),
  /**
   * @UC0055 — NGÀY SINH của suất CON ở nhóm phụ huynh (`group_members.birthDate`, nhập lúc thêm học sinh), cho khối "Thông tin cơ
   * bản" của màn Chi tiết thành viên. Ngày LỊCH trần `YYYY-MM-DD` — không giờ, không múi giờ (quy đổi ngày sinh ở máy múi giờ âm là
   * đọc ra ngày hôm trước). Cùng định dạng dây với `newMember.birthDate` / `identitySelectionOptionItemSchema.birthDate`.
   *
   * QĐ người dùng 18/10/2026: MỌI thành viên nhóm phụ huynh thấy (như tên học sinh; người cầm link mời đã thấy từ QĐ 14/08). Nhóm
   * THƯỜNG và dòng NGƯỜI LỚN ⇒ luôn `null` (cùng chốt review #1016 sec LOW-1: không đọc ngày sinh của người lớn ra), kể cả hàng cũ có
   * sẵn cột. BẮT BUỘC có mặt (nullable): một nhánh server quên điền phải đỏ ở biên.
   */
  birthDate: memberBirthDateSchema.nullable(),
};

/**
 * US-E23.F1-0331-S01/S04 (@UC9510) — thêm `totalContributedMinor` (Figma hiện tổng đã đóng của từng
 * người trong danh sách thành viên; contract cũ không có trường đó).
 *
 * 🔴 CÙNG luật redaction với `myGroupListItemSchema`: union theo `redacted`, nhánh ẩn = `'•••'`.
 * 🔴 `.strict()` VẪN chặn email/phone — bài học US-E4.F4-0022 (list member có test quét JSON chống
 * lộ PII). Thêm tiền vào response KHÔNG được nới cái chặn đó.
 */
export const memberListItemSchema = z.discriminatedUnion("redacted", [
  z.object({ ...memberListItemBase, redacted: z.literal(false), totalContributedMinor: GROUP_MONEY_INTEGER_STRING }).strict(),
  z.object({ ...memberListItemBase, redacted: z.literal(true), totalContributedMinor: z.literal(GROUP_MONEY_REDACTED) }).strict(),
]);

export type MemberListItem = z.infer<typeof memberListItemSchema>;

/**
 * Cờ action role-gated cho Member List (AC1/AC2) — canInviteMembers/canManageMembers = true CHỈ khi
 * role owner/admin. Server tính (single source of truth), client KHÔNG tự nâng quyền (C4).
 * canInviteMembers điều khiển nút "+" (route mời 0023); canManageMembers điều khiển mgmt action (0027).
 */
export const memberListActionsSchema = z
  .object({
    canInviteMembers: z.boolean(),
    canManageMembers: z.boolean(),
  })
  .strict();

export type MemberListActions = z.infer<typeof memberListActionsSchema>;

/** Query list members: cursor opaque optional, limit ∈ [1,50] optional, filter role optional. */
export const memberListQuerySchema = z.object({
  cursor: z.string().max(GROUP_MEMBERS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(GROUP_MEMBERS_MAX_LIMIT).optional(),
  /** Lọc theo role (owner|admin|treasurer|member). Vắng ⇒ tất cả role. Validate biên ở enum. */
  role: groupRoleSchema.optional(),
  /**
   * US-E24.F3-0352-S03 (@UC9574) — lọc theo trạng thái suất. **Vắng ⇒ `active`** (0 regress).
   * `?status=unclaimed` ⇒ chỉ suất chưa có người đại diện. Giá trị ngoài enum ⇒ 400 ở biên.
   */
  status: memberListStatusFilterSchema.optional(),
  /**
   * US-E4.F4-0387 (@UC0055) — tìm theo TÊN: khớp một-phần, case-insensitive, trên `fullName`
   * HOẶC `displayName` (hai field đã public sẵn trong response — KHÔNG mở search theo email/phone,
   * PII đó cố ý không có mặt ở bề mặt này). AND với role/status/cursor. Vắng ⇒ không lọc (0 regress).
   */
  search: z.string().trim().min(1).max(100).optional(),
});

export type MemberListQuery = z.infer<typeof memberListQuerySchema>;

/**
 * Envelope list members khớp quy ước repo `{ data, pageInfo }` + role/actions của caller.
 *  - data: danh sách member active (sort ổn định để cursor đáng tin).
 *  - role: vai trò của CHÍNH caller — client gate action (AC1/AC2).
 *  - actions: cờ role-gated của caller (nút "+"/mgmt). Server tính; client KHÔNG tự nâng quyền.
 * `.strict()` chặn rò field thừa ra response (C2).
 */
export const memberListResponseSchema = z
  .object({
    data: z.array(memberListItemSchema),
    role: groupRoleSchema,
    actions: memberListActionsSchema,
    /**
     * US-E4.F1-0431-S02 (@UC9677) — LOẠI NHÓM + gói từ vựng, đặt ở CẤP RESPONSE (không phải trong
     * từng item: nó là thuộc tính của NHÓM, lặp lại ở mỗi dòng là mời gọi lệch).
     *
     * 🔴 Vì sao endpoint NÀY chứ không chỉ hồ sơ nhóm: màn *Chi tiết thành viên* của mobile dựng
     * dữ liệu từ đây (`getGroupMembers(seatsOnly: true)`), nó KHÔNG có endpoint riêng. Đặt gói ở
     * `GET /groups/:id` thì màn đó không lấy được và phải gọi thêm một request chỉ để lấy danh từ.
     *
     * BẮT BUỘC có mặt (nullable, không optional): nhóm chưa gán loại ⇒ `null`. Cho phép vắng mặt
     * nghĩa là một nhánh code quên điền sẽ đi qua lặng lẽ.
     */
    category: groupCategoryEmbeddedSchema.nullable(),
    /**
     * 🔴 US-E4.F4-0551 (@UC0055) — QUẢN TRỊ VIÊN LUÔN CÓ MẶT, không phụ thuộc đã tải tới trang nào.
     *
     * Bug thật 26/08/2026: `data` sắp `createdAt DESC`, mà **chủ nhóm được tạo ĐẦU TIÊN** (lúc lập
     * nhóm) nên luôn rơi xuống **cuối cùng**. Nhập 14 học sinh bằng OCR là chủ nhóm bị đẩy xuống
     * đáy ⇒ người dùng phải tải hết mọi trang mới thấy ai đang quản trị nhóm mình.
     *
     * ## Vì sao KHÔNG sắp quản trị viên lên đầu `data`
     * Cursor của repo là `(createdAt, id)` — hai field **bất biến**. `role` thì **đổi được**: đang
     * cuộn giữa chừng mà ai đó được nâng lên admin thì hàng nhảy chỗ ⇒ cursor **nhảy cóc qua vài
     * người**. Trong danh sách thành viên của một quỹ, bỏ sót một người một cách lặng lẽ là đúng
     * thứ không được phép — và đó chính là lý do cursor dùng `createdAt/id` chứ không dùng gì khác.
     *
     * ## Vì sao trả kèm ở ĐÂY chứ không để client gọi thêm `?role=`
     * `?role` chỉ nhận MỘT giá trị nên client sẽ phải gọi hai lần rồi tự ghép. Cùng lý do đã ghi ở
     * `collection-schedules.ts:430`: *"ghép hai nguồn ở client là cách hai bên lệch nhau"*.
     *
     * ## Vì sao BẮT BUỘC chứ không "chỉ có ở trang đầu"
     * Cho phép vắng mặt nghĩa là client phải tự nhớ tập của trang 1 — một trạng thái để hiểu sai
     * ("mảng rỗng ở trang 2" nghĩa là gì?). Trả luôn luôn thì trạng thái sai **không biểu diễn
     * được**, đổi lại một truy vấn nhỏ trên tập có trần (owner đúng 1, admin vài người).
     *
     * Sắp theo vai (owner trước admin) rồi `createdAt` — **không** phân trang: đây là tập ghim, và
     * một nhóm không thể có nhiều quản trị viên tới mức cần cursor.
     */
    managers: z.array(memberListItemSchema),
    /**
     * 🔴 US-E4.F4-0595 (@UC0055 @UC0027) — NGƯỜI ĐANG CHỜ XÉT DUYỆT, ghim y như [managers].
     *
     * Cùng một bug với 0551, khác đối tượng: `data` sắp `createdAt DESC` nên người xin vào **hôm
     * qua** đã tụt xuống trang sau, và quản trị viên chỉ biết có người đang chờ nếu chịu khó cuộn.
     * Một lời xin vào không ai duyệt = một người đứng ngoài quỹ vô thời hạn ⇒ đây là **việc cần
     * làm**, phải nổi lên, không phải chờ được tìm thấy.
     *
     * ## Vì sao có CAP mà `managers` thì không
     * Xem [MEMBER_LIST_PENDING_PIN_LIMIT]. Tối đa 5 hàng, sắp `createdAt ASC` — **chờ lâu nhất
     * trước**, ngược chiều `data`: xếp hàng thì người đến trước được gọi trước.
     *
     * ## Vị-từ
     * `QUEUEING_SEAT_WHERE` (`apps/api/src/common/identity/membership-seat.resolver.ts`) —
     * `status='join_pending'` **VÀ** `joinedAt IS NULL` **VÀ** `leftAt IS NULL`. Hai vế sau loại
     * người **vừa rời / vừa bị gỡ** (QĐ PO 13/08 #6: `selfLeave` cũng ghi `join_pending`). Bỏ chúng
     * là lôi người vừa rời nhóm trở lại danh sách "đang xin vào" — mời quản trị viên duyệt lại một
     * người đã chủ động đi ra.
     *
     * ## 🔴 R9 — RBAC: hai field này ĐI CẶP
     * Chỉ O/A/T (`isManager`) thấy. Người không đủ quyền ⇒ `pendingMembers: []` **VÀ**
     * `pendingTotalCount: 0`. Trả mảng rỗng mà giữ tổng `12` vẫn phơi ra *"nhóm này có 12 người
     * đang xin vào"* — một sự thật quản trị, và là đúng thứ `JOIN_QUEUE_FORBIDDEN` đang chặn ở
     * `?status=join_pending`. Một nửa cánh cửa vẫn là cửa mở.
     *
     * BẮT BUỘC (không optional), cùng lẽ đã ghi cho [managers].
     *
     * 🔴 **`[] + 0` KHÔNG chỉ có nghĩa "không đủ quyền"** (review PR #675 M8). Nó còn là câu trả lời
     * khi client đã hỏi thẳng `?status=queueing` / `?status=join_pending`: lúc đó `data` **chính là**
     * hàng chờ, nên ghim lại 5 dòng đầu của chính nó là thừa. Client đừng suy `pendingTotalCount == 0`
     * ⇒ *"mình không có quyền"* — hai nguyên nhân, một hình dạng.
     */
    pendingMembers: z.array(memberListItemSchema).max(MEMBER_LIST_PENDING_PIN_LIMIT),
    /**
     * 🔴 US-E4.F4-0595 (@UC0055) — TỔNG THẬT số suất đang chờ duyệt, KHÔNG phải `pendingMembers.length`.
     *
     * Hai con số khác nhau ngay khi vượt cap, và client cần con số THẬT để (a) đặt số ở tiêu đề mục
     * và (b) quyết có hiện nút "Xem tất cả" hay không. Suy từ độ dài mảng thì mục sẽ nói *"5"* cho
     * một nhóm có 40 người đang chờ.
     *
     * Đi CẶP với [pendingMembers] ở luật R9 — xem docblock ở trên.
     */
    pendingTotalCount: z.number().int().min(0),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
      /**
       * US-E4.F4-0387 (@UC0055) — số item CÒN LẠI sau trang này, TÔN TRỌNG mọi filter đang áp
       * (search/role/status). FE render nút "Xem thêm min(remainingCount, limit)" — trang áp chót
       * lộ đúng số lẻ (vd "+5" thay vì "+10"). `hasNext=false ⇔ remainingCount=0`.
       * Cơ chế global: `PageInfo` chung (`common/pagination/cursor.ts`) khai field này OPTIONAL —
       * list khác adopt dần không vỡ shape; response NÀY bắt buộc vì đã wire.
       */
      remainingCount: z.number().int().min(0),
    }),
  })
  .strict();

export type MemberListResponse = z.infer<typeof memberListResponseSchema>;

/** Tính cờ action Member List từ role caller (AC1/AC2). owner/admin → mọi cờ true; còn lại false. */
export function computeMemberListActions(role: GroupRole): MemberListActions {
  const canManage = GROUP_MANAGE_ROLES.includes(role);
  return {
    canInviteMembers: canManage,
    canManageMembers: canManage,
  };
}

/* ===========================================================================
 * PATCH /v1/groups/:id/membership — TÊN CỦA TÔI TRONG NHÓM NÀY (US-E2.F1-0399 · @UC0055).
 *
 * Đặt tên theo `:id/membership` cho khớp `DELETE :id/membership` (rời nhóm) đã có — cùng một nghĩa
 * "quan hệ của CHÍNH TÔI với nhóm này". KHÔNG đụng `User.fullName` toàn cục: đổi tên trong quỹ lớp
 * không được đổi tên ở quỹ công ty.
 *
 * 🔴 BẤT BIẾN SỐ MỘT — KHÔNG BAO GIỜ ghi tên lên `group_members.display_name` của suất có
 * `linkRole ≠ self`. Suất đó là MỘT ĐỨA TRẺ: phụ huynh gõ "Ba bé Long" mà ghi đè lên suất thì tên
 * đứa trẻ hoá tên bố, và cái tên sai đó xuất hiện trên mọi đợt thu + sổ nợ của lớp.
 *
 * ⇒ Server rẽ nhánh theo `linkRole` **ĐÃ LƯU** của account người gọi (dữ liệu), KHÔNG suy từ người
 * bấm nút hay từ body:
 *   · `self`                              → `group_members.display_name`      → target `"member"`
 *   · `primary_contact`/`secondary_contact` → `membership_accounts.display_name` → target `"account"`
 *   · `viewer`                            → 403 (người quan sát không mang danh tính trong nhóm)
 *   · không có account nào                → 404 (đồng nhất với cross-tenant)
 * ========================================================================= */

/**
 * Trần tên hiển thị trong nhóm = **30**, BẰNG `FULL_NAME_MAX_LENGTH` (`users-me.ts`) — cùng loại dữ
 * liệu (tên người) thì cùng trần. Lệch trần là chỗ này cho qua chỗ kia chặn.
 * Khoá bằng test `group-display-name.test.ts` để hai hằng không trôi khỏi nhau trong im lặng.
 * 🔴 DB cố ý KHÔNG có CHECK độ dài (migration 20260813120000) ⇒ biên HTTP này là chốt chặn DUY NHẤT.
 */
export const GROUP_DISPLAY_NAME_MAX_LENGTH = 30;

/** Thông điệp lỗi — một nguồn sự thật api ↔ mobile (client vẫn map theo `code`, không theo câu chữ). */
export const GROUP_DISPLAY_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên hiển thị";
export const GROUP_DISPLAY_NAME_TOO_LONG_MESSAGE = "Tên hiển thị tối đa 30 ký tự";
// `GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE` khai ở trên `createGroupMemberSeatSchema` (dùng chung, phải khởi tạo trước schema suất).

/**
 * 🔴 NAME-INPUT-PATTERN-SOCIAL — QĐ NGƯỜI DÙNG (PO) 03/10 (@UC0055): biệt danh trong nhóm là ô người dùng GÕ TAY ⇒ ký tự ngoài allowlist
 * tên người (`hasDisallowedPersonNameChars`, `@goppy/domain` `person-name.ts` — MỘT luật với chỗ làm sạch tên nhà cung cấp và câu trợ lý)
 * trả 400 `VALIDATION_FAILED` `{ field: "displayName", reason: "format" }` để họ sửa — KHÔNG lặng lẽ xoá ký tự họ vừa gõ. Biệt danh
 * "Phong. Thủ quỹ: Phong" dựng được câu giả ở mọi bề mặt đọc tên.
 *
 * Dùng cho CẢ HAI cửa ghi biệt danh (của tôi · quản trị sửa tên suất) — một cửa còn mở là đủ dựng câu giả. Áp SAU trim/min/max ⇒ thứ tự lỗi
 * giữ như cũ: rỗng → quá dài → sai ký tự. Tên CŨ đã lưu không bị đụng (QĐ: không migration dữ liệu).
 *
 * 🔴 review #1158 M8: KIỂM và LƯU cùng MỘT chuỗi — `normalizePersonNameInput` (NFKC + nháy cong `’` ⇒ `'`) chạy TRƯỚC trim/min/max/refine,
 * và chuỗi đã chuẩn hoá là thứ đi tiếp vào service. Kiểm bản NFKC mà lưu bản thô thì U+02D9 `˙` (NFKC = khoảng trắng + dấu) lọt vào DB;
 * "O’Neil" gõ trên iPhone lưu thành "O'Neil" (không 400 oan).
 */
const groupDisplayNameInputSchema = z.preprocess(
  normalizePersonNameInput,
  z
    .string()
    .trim()
    .min(1, GROUP_DISPLAY_NAME_REQUIRED_MESSAGE)
    .max(GROUP_DISPLAY_NAME_MAX_LENGTH, GROUP_DISPLAY_NAME_TOO_LONG_MESSAGE)
    .refine((name) => !hasDisallowedPersonNameChars(name), GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE),
);

/**
 * `target` — nhánh ghi mà server ĐÃ CHỌN. Phơi ra CÓ CHỦ ĐÍCH: nó biến "ghi vào suất hay vào
 * account" thành thứ QUAN SÁT ĐƯỢC từ test và từ client, thay vì hành vi ngầm chỉ soi DB mới biết.
 *  - `member`  : đã ghi tên của SUẤT   (`group_members.display_name`).
 *  - `account` : đã ghi tên của NGƯỜI  (`membership_accounts.display_name`) — tên suất KHÔNG đổi.
 */
export const GROUP_DISPLAY_NAME_TARGET = {
  MEMBER: "member",
  ACCOUNT: "account",
} as const;

export type GroupDisplayNameTarget =
  (typeof GROUP_DISPLAY_NAME_TARGET)[keyof typeof GROUP_DISPLAY_NAME_TARGET];

export const groupDisplayNameTargetSchema = z.enum([
  GROUP_DISPLAY_NAME_TARGET.MEMBER,
  GROUP_DISPLAY_NAME_TARGET.ACCOUNT,
]);

/**
 * Body `PATCH /v1/groups/:id/membership`.
 *  - `displayName` BẮT BUỘC: trim trước rồi đếm 1..30 (rỗng/chỉ-khoảng-trắng ⇒ 400; >30 ⇒ 400).
 *    Không có nhánh "vắng field = giữ nguyên": PATCH rỗng là no-op câm, người dùng tưởng đã đổi.
 *  - `.strict()`: KHÔNG nhận `userId`/`target` từ client — server tự quyết ghi vào ĐÂU theo
 *    `linkRole` đã lưu. Nhận `target` từ body chính là mở đường ghi đè tên đứa trẻ.
 *
 * 🔴 `memberId` TUỲ CHỌN, và nó KHÔNG phải "cho client chọn nhánh ghi" — nó trả lời câu hỏi KHÁC:
 * **đổi tên của tôi Ở SUẤT NÀO.**
 *
 * Một user có thể có NHIỀU suất trong cùng một nhóm: `membership_accounts` chỉ unique theo
 * `(groupMemberId, userId)`, không giới hạn số suất — sinh đôi là ca thật, bố mẹ gắn vào cả hai
 * đứa. (Chỉ `primaryUserId` mới bị chặn 1-suất-1-nhóm bởi partial unique
 * `one_active_seat_per_user_per_group`, mà `identity-selection` cố ý KHÔNG set cột đó.)
 *
 * Vắng `memberId` khi người gọi có ĐÚNG MỘT suất ⇒ dùng suất đó. Có ≥2 suất ⇒ **409 fail-closed**,
 * KHÔNG chọn bừa: chọn nhầm nghĩa là đặt tên lên quan hệ với đứa con KHÁC. Đây đúng nguyên tắc
 * "0 hoặc ≥2 khớp thì phải hỏi lại, không đoán" mà repo đã ghi cho lớp AI — cùng một loại nguy hiểm.
 */
export const updateMyGroupDisplayNameRequestSchema = z
  .object({
    displayName: groupDisplayNameInputSchema,
    memberId: z.string().uuid().optional(),
  })
  .strict();

export type UpdateMyGroupDisplayNameRequest = z.infer<
  typeof updateMyGroupDisplayNameRequestSchema
>;

/** Response 200: tên đã lưu (sau trim) + nhánh ghi. `.strict()` chặn rò field thừa. */
export const updateMyGroupDisplayNameResponseSchema = z
  .object({
    displayName: z.string(),
    target: groupDisplayNameTargetSchema,
  })
  .strict();

export type UpdateMyGroupDisplayNameResponse = z.infer<
  typeof updateMyGroupDisplayNameResponseSchema
>;

/**
 * 🔴🔴 US-E2.F3-0859 (@UC0027 @UC9516) — query của `DELETE /v1/users/me/groups/:groupId/join-request`:
 * **HUỶ LỜI XIN CHO SUẤT NÀO.**
 *
 * VÌ SAO PHẢI CÓ: trước bản này hàm huỷ chỉ nhận `(groupId, userId)` và tìm suất bằng `findFirst`
 * **không `orderBy`**. Phụ huynh có HAI con cùng chờ duyệt trong một lớp là ca thật
 * (`membership_accounts` chỉ unique theo `(groupMemberId, userId)` — không giới hạn số suất), nên một
 * cú bấm "Huỷ yêu cầu" huỷ **ngẫu nhiên một trong hai suất của chính họ**, im lặng, không có cách nào
 * biết là suất nào.
 *
 * 🔴 BẢN VÁ BỎ HẲN VIỆC CHỌN, KHÔNG ĐI VÁ THỨ TỰ. Thêm `orderBy` chỉ biến "ngẫu nhiên" thành "luôn
 * luôn sai theo cùng một cách" — vẫn là đoán chủ thể thay người dùng.
 *
 * 🔴🔴 **VẮNG `memberId` ⇒ RÚT TẤT CẢ suất đang chờ của người gọi** (QĐ người dùng 28/09/2026).
 *
 * Bản đầu trả **409 `SEAT_AMBIGUOUS`** ở ca ≥2 suất, mượn nguyên tắc *"0 hoặc ≥2 khớp thì phải hỏi
 * lại, không đoán"* của `updateMyGroupDisplayNameRequestSchema` ngay trên. Nguyên tắc đó **đúng cho
 * route đổi tên** và **sai cho route này** — khác nhau ở một điểm dứt khoát: đổi tên là *"đặt giá trị
 * X lên suất nào"* (chọn sai ⇒ hỏng dữ liệu của đứa con kia), còn huỷ là *"rút lời xin của tôi"*, mà
 * lời xin là MỘT và UI có ĐÚNG MỘT nút. Không có câu hỏi nào để hỏi lại.
 *
 * Và 409 ở đây là **ngõ cụt kèm lời nói sai**: `apps/mobile/.../pending_group_screen.dart` map cả 404
 * lẫn 409 thành "màn hình đã cũ" ⇒ hiện *"Yêu cầu này không còn nữa…"* rồi đẩy người dùng về danh sách,
 * nơi nhóm **vẫn hiện "Chờ duyệt"** ⇒ lặp vô hạn. Mobile cũng KHÔNG THỂ tự gửi `memberId`: màn đó cố ý
 * không gọi API nhóm nào nên nó không có id suất nào trong tay.
 *
 * ⇒ `memberId` là đường **THU HẸP tuỳ chọn** cho UI sau này (huỷ riêng một con), không phải điều kiện
 * bắt buộc. Rút hết cũng đóng luôn khuyết điểm "huỷ nhầm suất đứa kia": không còn phép chọn để chọn sai.
 *
 * `.strict()` ⇒ gửi field lạ (`userId`, `status`…) là **400 ở biên, 0 row chạm DB**: route này không
 * bao giờ được nhận chủ thể từ client — chủ thể là JWT.
 */
export const cancelMyJoinRequestQuerySchema = z
  .object({
    memberId: z.string().uuid().optional(),
  })
  .strict();

export type CancelMyJoinRequestQuery = z.infer<typeof cancelMyJoinRequestQuerySchema>;

/**
 * US-E4.F4-0541 (@UC0055, QĐ người dùng 25/08/2026) — `PATCH /v1/groups/:id/members/:mid/display-name`.
 *
 * 🔴 TÁCH KHỎI `updateMyGroupDisplayNameRequestSchema`, cố ý, dù hai body trông giống nhau:
 *  - Cái kia là *"tên CỦA TÔI trong nhóm này"* — suất do SERVICE tự tra theo `actorUserId`, nên nó
 *    mang thêm `memberId` để người có >=2 suất tự chỉ đích danh SUẤT CỦA CHÍNH MÌNH.
 *  - Cái này là *"quản trị viên sửa tên MỘT SUẤT KHÁC"* — suất nằm ở PATH (`:mid`), và người gọi
 *    không cần có suất nào trong nhóm cũng đúng nghiệp vụ.
 * Gộp hai schema lại thì `memberId` trong body sẽ chồng lên `:mid` ở path — hai nguồn cho cùng một
 * câu hỏi "sửa suất nào", và đó là hình dạng của một lỗ ghi-đè-nhầm-suất.
 *
 * 🔴 KHÔNG có field nào chạm `users.fullName`. `.strict()` => gửi kèm `fullName` là **400 ở biên,
 * 0 row chạm DB** — không phải "server lặng lẽ bỏ qua field lạ". Đổi tên trong một nhóm không bao
 * giờ được đổi tên tài khoản, vì người đó có thể đang ở nhiều nhóm khác.
 */
export const updateMemberDisplayNameRequestSchema = z
  .object({
    displayName: groupDisplayNameInputSchema,
  })
  .strict();

export type UpdateMemberDisplayNameRequest = z.infer<typeof updateMemberDisplayNameRequestSchema>;

/** Response 200: tên đã lưu (sau trim). `.strict()` chặn rò field thừa. */
export const updateMemberDisplayNameResponseSchema = z
  .object({ displayName: z.string() })
  .strict();

export type UpdateMemberDisplayNameResponse = z.infer<typeof updateMemberDisplayNameResponseSchema>;

/**
 * @UC0055 (QĐ người dùng 18/10/2026) — `PATCH /v1/groups/:id/members/:mid/birth-date`: CHỦ NHÓM / QUẢN TRỊ VIÊN sửa ngày sinh của MỘT
 * SUẤT CON ở nhóm phụ huynh (cùng quyền với sửa tên suất — `updateMemberDisplayNameRequestSchema`).
 *
 * `birthDate` BẮT BUỘC có mặt: `null` = XOÁ TRỐNG; vắng khoá ⇒ 400 (không để "quên gửi" bị hiểu thành "xoá"). Suất nằm ở PATH (`:mid`),
 * không có `memberId` trong body (hai nguồn cho câu hỏi "sửa suất nào" là hình dạng của lỗ ghi-đè-nhầm-suất). Luật "không ở tương lai"
 * so theo NGÀY VIỆT NAM ở server (`GROUP_MEMBER_BIRTH_DATE_CODE.IN_FUTURE`).
 */
export const updateMemberBirthDateRequestSchema = z.object({ birthDate: memberBirthDateSchema.nullable() }).strict();

export type UpdateMemberBirthDateRequest = z.infer<typeof updateMemberBirthDateRequestSchema>;

/** Response 200: ngày sinh đã lưu (hoặc `null` khi vừa xoá). */
export const updateMemberBirthDateResponseSchema = z.object({ birthDate: memberBirthDateSchema.nullable() }).strict();

export type UpdateMemberBirthDateResponse = z.infer<typeof updateMemberBirthDateResponseSchema>;

/** Mã lỗi máy-đọc-được (RFC7807 `code`) của route sửa ngày sinh — client map code→message. */
export const GROUP_MEMBER_BIRTH_DATE_CODE = {
  /** Nhóm THƯỜNG hoặc dòng NGƯỜI LỚN (ghế `self`) — ngày sinh chỉ có nghĩa ở suất con nhóm phụ huynh (QĐ 18/10) ⇒ 422. */
  NOT_APPLICABLE: "GROUP_MEMBER_BIRTH_DATE_NOT_APPLICABLE",
  /** Ngày sinh sau HÔM NAY theo lịch Việt Nam ⇒ 422. */
  IN_FUTURE: "GROUP_MEMBER_BIRTH_DATE_IN_FUTURE",
  /** Suất đã rời / bị gỡ / đã được nhận (`claimed`) ⇒ 409 — cùng tập sửa được với cửa admin hệ thống (`@UC9947`). */
  SEAT_NOT_EDITABLE: "GROUP_MEMBER_BIRTH_DATE_SEAT_NOT_EDITABLE",
} as const;

/** Mã lỗi máy-đọc-được (RFC7807 `code`) — client map code→message, KHÔNG so khớp `detail`. */
export const GROUP_DISPLAY_NAME_CODE = {
  /** `linkRole='viewer'`: quan sát viên không mang danh tính trong nhóm ⇒ 403. */
  VIEWER_NO_IDENTITY: "GROUP_DISPLAY_NAME_VIEWER_NO_IDENTITY",
  /**
   * Người gọi có ≥2 suất trong nhóm mà body không nói suất nào ⇒ **409, KHÔNG chọn bừa**.
   *
   * Ca thật: bố mẹ của một cặp sinh đôi, gắn vào cả hai suất. "Tên của tôi trong nhóm này" lúc đó
   * là hai câu trả lời khác nhau ("Mẹ bé An" / "Mẹ bé Bình"), và máy không có cơ sở nào để chọn.
   */
  SEAT_AMBIGUOUS: "GROUP_DISPLAY_NAME_SEAT_AMBIGUOUS",
} as const;

export const GROUP_DISPLAY_NAME_VIEWER_MESSAGE =
  "Bạn đang xem nhóm với vai người quan sát nên chưa có tên hiển thị để đổi.";

export const GROUP_DISPLAY_NAME_SEAT_AMBIGUOUS_MESSAGE =
  "Bạn đang đại diện cho nhiều người trong nhóm này. Hãy chọn bạn muốn đổi tên ở người nào.";

/* ===========================================================================
 * GET /v1/groups/:id/members/:memberId/contributions — CHI TIẾT ĐÓNG GÓP CỦA MỘT SUẤT
 * (US-E6.F9-0412 · @UC0060 @UC0055 @UC0058).
 *
 * VÌ SAO CÓ: màn "Chi tiết thành viên" ở mobile đang hiện SỐ TIỀN BỊA trên một màn nói về một người
 * CÓ THẬT. Trước task này backend chỉ trả lời được *"đợt thu X ai đã đóng"*
 * (`/collections/:id/members-status`) và *"CHÍNH TÔI trong quỹ Y"* (`/funds/:id/dues/me`) — không có
 * đường nào trả lời *"người này đã đóng bao nhiêu, còn thiếu bao nhiêu, xuyên suốt nhóm"*.
 *
 * 🔴 HAI TRỤC ĐỘC LẬP (CLAUDE.md §7 P5.3 — *"nhóm có thể minh bạch tiền mà vẫn giấu tên, hoặc ngược
 * lại"*). Đừng gộp:
 *  - **DANH TÍNH** (ai được mở hồ sơ của ai) — luật của `MemberDueReadAccessGuard`, tái dùng nguyên
 *    văn: chủ SUẤT xem của mình · O/A/T xem của người khác · còn lại **404 đồng nhất**. Trục này KHÔNG
 *    biểu diễn được bằng redaction: không cho xem thì không trả về gì cả.
 *  - **TIỀN** (thấy SỐ hay `'•••'`) — `transparency_mode`, CÙNG luật với `totalContributedMinor` ở
 *    `GET /v1/groups/:id/members`. Đó là lý do hai nhánh dưới đây tồn tại.
 * ========================================================================= */

/** Max page-size cho lịch sử đóng góp. Default (20) do `normalizeLimit` backend áp khi vắng. */
export const MEMBER_CONTRIBUTIONS_MAX_LIMIT = 50;

/**
 * 🔴 US-E24.F51-0503 (@UC0060) — CAP PHÒNG THỦ cho `outstanding[]`, **không phải phân trang**.
 *
 * `outstanding[]` trả TRỌN có chủ đích (AC8): nó trả lời *"tôi còn phải trả những gì"*, và câu trả
 * lời đó không được phụ thuộc người dùng đã cuộn lịch sử tới đâu. Số khoản còn thiếu của MỘT người
 * là hữu hạn nhỏ — 200 kỳ chưa đóng là bệnh lý dữ liệu, không phải cách dùng bình thường.
 *
 * Cap tồn tại để một suất bệnh lý không đẻ ra response khổng lồ. Hai điều làm nó KHÔNG nói dối:
 *  1. `totalOwedMinor` luôn là tổng của **TOÀN BỘ** dues (nguồn `FinancialPositionService`), không
 *     phải tổng của mảng này ⇒ con số "còn thiếu bao nhiêu" vẫn đúng kể cả khi mảng bị cắt.
 *  2. Mảng sắp theo **hạn gần nhất trước** ⇒ thứ bị cắt là thứ ÍT cấp bách nhất.
 */
/**
 * 🔴 US-MOB.F9-1048 — CẢ HAI CAP ĐÃ GỠ (`..._OUTSTANDING_CAP = 200` · `..._REJECTED_CAP = 50`).
 *
 * Chúng cắt ÂM THẦM: response không có cờ nào nói "đã cắt", nên người có 60 lời khai bị bác thấy 50
 * và KHÔNG có cách nào biết. Nay cả bốn loại hàng đi qua cùng MỘT cursor ⇒ không loại nào cần cap, và
 * `pageInfo.hasNext` nói thật cho toàn bộ dòng thời gian.
 *
 * Giá trị filter của ba chip ở màn «Lịch sử đóng góp». `PAID` gom CẢ `due` LẪN `other_income` — đúng
 * như chip *"Đã đóng"* vẫn vẽ: cả hai đều là tiền đã vào quỹ, người dùng không phân biệt chúng.
 */
export const MEMBER_CONTRIBUTION_FILTER_KIND = {
  PAID: "paid",
  OUTSTANDING: "outstanding",
  REJECTED: "rejected",
} as const;

export type MemberContributionFilterKind =
  (typeof MEMBER_CONTRIBUTION_FILTER_KIND)[keyof typeof MEMBER_CONTRIBUTION_FILTER_KIND];

export const memberContributionFilterKindSchema = z.enum([
  MEMBER_CONTRIBUTION_FILTER_KIND.PAID,
  MEMBER_CONTRIBUTION_FILTER_KIND.OUTSTANDING,
  MEMBER_CONTRIBUTION_FILTER_KIND.REJECTED,
]);

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS), mirror member list. */
export const MEMBER_CONTRIBUTIONS_MAX_QUERY_LENGTH = 512;

/**
 * Field KHÔNG-TIỀN của một khoản trong lịch sử — phần dùng chung cho cả hai nhánh redaction.
 *
 * 🔴 CỐ Ý KHÔNG mang field danh tính (`memberName`/`avatarUrl`/`groupMemberId`): đây là lịch sử của
 * ĐÚNG MỘT người, danh tính nằm một lần ở cấp response. Nhét tên vào từng dòng sẽ biến response này
 * thành "danh sách người kèm tiền" — thứ mà `person-payment-exposure.invariants.spec.ts` (@UC9582)
 * canh, và đúng ra phải canh.
 */
/**
 * 🔴 US-E7.F1-0742 (@UC0074 @UC0060 · QĐ người dùng 08/09/2026) — LOẠI DÒNG trong lịch sử đóng góp.
 *
 * Lịch sử này từng CHỈ đọc `member_dues`, nên khoản đóng góp **"Khác"** (`SPONSORSHIP_MEMBER` — post
 * thẳng ledger, KHÔNG sinh due) không bao giờ có dòng. Sau US-E7.F1-0741, khoản đó đã được cộng vào
 * `totalPaidMinor` — và màn hình rơi vào trạng thái tự mâu thuẫn mà người dùng nhìn thấy ngay:
 * *"Khoản đã đóng 199.999đ"* đứng trên một danh sách nói *"Chưa có khoản đóng góp nào"*.
 *
 * 🔴 HAI NHÁNH, KHÔNG PHẢI MỘT KHUÔN CÓ FIELD NULLABLE. Chúng khác nhau thật sự:
 *  · `due` — thuộc một ĐỢT THU, có `memberDueId`/`collectionId`/`status` (đóng đủ vs đóng dở);
 *  · `other_income` — KHÔNG thuộc đợt thu nào, nên không có ba thứ đó; nó có `ledgerTxId`.
 *
 * Nhét chúng vào một khuôn rồi để `collectionId` nullable sẽ đẻ ra tổ hợp không tồn tại ("khoản
 * ngoài đợt thu nhưng có trạng thái đóng dở"), và mọi nơi dùng phải nhớ kiểm null. Discriminated
 * union làm những tổ hợp đó **không biểu diễn được**, và buộc client `switch` — không có đường quên.
 */
/**
 * 🔴🔴 US-MOB.F9-1048 (QĐ người dùng 03/11/2026 vòng 3 — *"cứ theo dòng thời gian, không đảo cái gì
 * gấp lên đầu"*): union này nay có **BỐN** nhánh, không hai.
 *
 * Trước 1048 response có BA mảng: `data[]` (phân trang) + `outstanding[]` + `rejected[]` (trả TRỌN,
 * mỗi mảng một cap). Màn hình vẽ chúng nối nhau thành một khối cuộn với MỘT nút tải thêm — mà nút đó
 * chỉ kéo dài được một phần ba nội dung. Người dùng đọc ra *"một list, một nút"*, và mô hình thật
 * lệch khỏi đó: `hasNext` chỉ nói về `paid`, hai mảng kia bị cap cắt ÂM THẦM, và mỗi lượt tải thêm
 * tải lại trọn hai mảng kia rồi bỏ đi.
 *
 * ⇒ MỘT mảng, MỘT cursor, bốn loại hàng phân biệt bằng `kind`. Mỗi hàng mang mốc riêng của nó
 * (`occurredAt`) và cả danh sách xếp theo đúng mốc đó.
 */
export const CONTRIBUTION_ENTRY_KIND = {
  /** Khoản của một đợt thu đã CÓ TIỀN VÀO (§3.7 trạng thái 3). */
  DUE: "due",
  /** Đóng góp thêm, ngoài mọi nghĩa vụ — post thẳng sổ cái, không sinh due. */
  OTHER_INCOME: "other_income",
  /** Khoản còn thiếu (trước 1048 ở `outstanding[]`). */
  OUTSTANDING: "outstanding",
  /** Lời khai bị NGƯỜI KHÁC từ chối (trước 1048 ở `rejected[]`). */
  REJECTED: "rejected",
} as const;

export type ContributionEntryKind =
  (typeof CONTRIBUTION_ENTRY_KIND)[keyof typeof CONTRIBUTION_ENTRY_KIND];

/**
 * Field dùng chung CẢ BỐN nhánh — mốc của hàng trong dòng thời gian.
 *
 * 🔴 US-MOB.F9-1048 — MỘT field cho cả ba vai: khoá SORT, khoá CURSOR, và ngày hàng HIỂN THỊ. Ba vai
 * này từng là ba field khác nhau (`paidAt` hiện, `createdAt` keyset, `rejectedAt` của riêng lời khai),
 * và đó là cách một danh sách nói dối: dòng in ngày 02/11 mà nằm ở vị trí 15/01 thì không ai đối chiếu
 * được. Gộp lại ⇒ thứ người đọc thấy CHÍNH LÀ thứ quyết định vị trí.
 *
 * Mốc THẬT của từng loại (`member-contributions.service.ts` là nơi gán):
 *  · `due` — lúc tiền được xác nhận vào quỹ (`member_dues.paidAt`, đặt cùng tx với bút toán);
 *  · `other_income` — lúc ghi bút toán;
 *  · `outstanding` — lúc khoản được tạo (nghĩa vụ xuất hiện trong đời người dùng);
 *  · `rejected` — lúc bị từ chối (`reviewedAt`).
 *
 * 🔴 `null` = KHÔNG BIẾT mốc, không phải "mốc bằng 0". Chỉ xảy ra với khoản đã đóng của dữ liệu cũ
 * chưa có `paidAt`. Hàng đó xếp CUỐI dòng thời gian (NULLS LAST) và client KHÔNG in ngày — cùng một
 * câu trả lời ở cả hai chỗ. Rơi về `createdAt` sẽ in một ngày KHÁC vị trí nó đứng; bịa "hôm nay" thì
 * tệ hơn nữa.
 */
const contributionEntryShared = {
  occurredAt: z.string().datetime().nullable(),
};

/** Nhánh DUE — khoản thuộc một đợt thu. */
const dueEntryBase = {
  kind: z.literal(CONTRIBUTION_ENTRY_KIND.DUE),
  memberDueId: z.string().uuid(),
  collectionId: z.string().uuid(),
  /** Tên đợt thu; `null` khi đợt thu chưa đặt tiêu đề (KHÔNG bịa chuỗi thay thế ở server). */
  collectionTitle: z.string().nullable(),
  /**
   * Trạng thái THẬT của khoản (passthrough từ `member_dues`, KHÔNG recompute). Có nó thì client phân
   * biệt được "đóng đủ" với "đóng dở 150k/500k" — không có thì hai chuyện đó trông y hệt nhau.
   */
  status: memberDueStatusSchema,
  /**
   * 🔴 THÊM 17/09/2026 — CÙNG siêu dữ liệu kỳ với `memberContributionOutstandingBase` bên dưới (một
   * nguồn `loadPeriodIndexes`, "Kỳ 3" phải chỉ đúng một kỳ ở CẢ `data[]` lẫn `outstanding[]`). Trước
   * bản vá `data[]` (lịch sử ĐÃ ĐÓNG) không mang field này dù `outstanding[]` đã có — đợt định kỳ đã
   * đóng lặp tên qua nhiều dòng lịch sử mà không cách nào phân biệt. `null` = đợt thu một lần.
   */
  scheduleId: z.string().uuid().nullable(),
  periodKey: z.string().nullable(),
  periodIndex: z.number().int().positive().nullable(),
  /**
   * 🔴 FUND-DEBT-SELF-CONFIRM (@UC9264 · §3.7 · QĐ PO 06/10 + QĐ điều phối) — khoản "nợ quỹ" (đợt `fund_debt`) được NGƯỜI NỢ TỰ XÁC NHẬN (ngoại lệ
   * hẹp: họ là quản lý DUY NHẤT của nhóm) — có bút toán CONTRIBUTION nhãn `self_one_party` còn hiệu lực. Ghi CÔNG KHAI ở kênh XEM (đúng luật hiển
   * thị hiện có của bề mặt này), mobile hiện nhãn "Tự xác nhận". `false` cho mọi khoản khác. `.default(false)` — server cũ chưa gửi ⇒ như trước.
   */
  selfConfirmed: z.boolean().default(false),
  ...contributionEntryShared,
};

/**
 * Nhánh KHÁC — đóng góp thêm, ngoài mọi nghĩa vụ.
 *
 * 🔴 KHÔNG có `status`: khoản này không có nghĩa vụ nào để "đóng đủ" hay "đóng dở". Bịa ra một
 * `status: paid` cho nó là khẳng định nó đã hoàn thành một nghĩa vụ không tồn tại.
 *
 * 🔴 KHÔNG có `collectionId` — và đây là chỗ hay bị hiểu nhầm: khoản này **CÓ thuộc một quỹ**
 * (`POST /v1/funds/:id/income` ⇒ `ledger_transactions.fundId`), nó chỉ không thuộc **đợt thu** nào.
 */
const otherIncomeEntryBase = {
  kind: z.literal(CONTRIBUTION_ENTRY_KIND.OTHER_INCOME),
  /** Bút toán đã ghi khoản này. Dùng làm khoá dòng; KHÔNG phải `memberDueId`. */
  ledgerTxId: z.string().uuid(),
  /**
   * Ghi chú NGƯỜI GHI nhập cho khoản (`ledger_transactions.memo`). `null` ⇒ không ai ghi chú.
   *
   * 🔴 US-E24.F50-0793 (QĐ người dùng 15/09/2026): server KHÔNG còn đặt câu mặc định theo loại thu
   * ("Tài trợ từ thành viên"…) vào memo — khoản này hiện dưới tên *"Quỹ chung <tên nhóm>"*, do
   * client ghép từ tên nhóm HIỆN TẠI (đổi tên nhóm ⇒ đổi theo; sổ cái append-only không sửa được
   * chữ đã ghi). Bút toán CŨ mang đúng câu mặc định cũ ⇒ server trả `null` (compat), không lộ câu
   * tiếng Việt cứng lên client.
   */
  note: z.string().nullable(),
  ...contributionEntryShared,
};

/* `memberContributionEntrySchema` khai Ở DƯỚI, sau `memberContributionOutstandingBase` và
 * `memberContributionRejectedBase`: từ US-MOB.F9-1048 union có bốn nhánh và hai nhánh mới dựng từ hai
 * base đó, nên nó buộc phải đứng sau chúng trong file. */

/* ---------------------------------------------------------------------------
 * US-E24.F51-0503 (@UC0060 @UC0055 @UC9659) — KHOẢN **CÒN THIẾU**.
 *
 * VÌ SAO PHẢI CÓ DANH SÁCH, KHÔNG CHỈ `totalOwedMinor`: tổng nói *"còn thiếu 1.050.000đ"* nhưng
 * không nói **thiếu ở đợt nào, hạn bao giờ**. Người dùng biết mình nợ mà không biết đi trả cái gì —
 * và một app về NIỀM TIN thì không được để người ta ở trạng thái đó.
 *
 * 🔴 MỘT KHOẢN CÓ THỂ NẰM Ở CẢ `data[]` LẪN `outstanding[]`, VỚI HAI CON SỐ KHÁC NHAU — và đó là
 * ĐÚNG, không phải trùng lặp. Khoản đóng dở 150k/500k: `data[]` mang `150000` (tiền ĐÃ vào —
 * `collectedMinorOf`), `outstanding[]` mang `350000` (phần CÒN thiếu — `outstandingMinorOf`). Hai
 * danh sách trả lời hai câu hỏi khác nhau về cùng một khoản tiền. Bất biến khoá bằng test:
 * `collectedMinorOf + outstandingMinorOf = amountMinor` cho mọi status trừ `waived`.
 *
 * ⚠️ `waived` KHÔNG thuộc bên nào (và không vào `totalOwedMinor`): miễn thì không kỳ vọng thu. Đưa nó
 * vào `outstanding[]` là đòi tiền một khoản đã tha.
 *
 * ⚠️ `awaiting_confirmation` thuộc **CÒN THIẾU** (§3.7 — ba trạng thái tiền không được gộp): người
 * bấm *"tôi đã chuyển"* mới ở trạng thái (2) KHAI BÁO, chưa phải (3) XÁC NHẬN NHẬN. Máy không bao
 * giờ tự in "đã nhận".
 * ------------------------------------------------------------------------- */

/**
 * Field KHÔNG-TIỀN của một khoản còn thiếu — dùng chung cho cả hai nhánh redaction, để hai nhánh
 * không thể trôi khỏi nhau.
 *
 * 🔴 `status` cố ý dùng **NGUYÊN** `memberDueStatusSchema` chứ không phải một enum con "chỉ các
 * trạng thái còn thiếu". Whitelist chọn khoản nào là còn thiếu sống ở MỘT chỗ duy nhất
 * (`OUTSTANDING_DUE_STATUSES` — `collection-progress.math.ts`); chép một bản thứ hai xuống contract
 * là dựng đúng cái *"drift 2 whitelist"* mà §3.8 cấm.
 */
const memberContributionOutstandingBase = {
  memberDueId: z.string().uuid(),
  collectionId: z.string().uuid(),
  /** Tên đợt thu; `null` khi đợt chưa đặt tiêu đề (KHÔNG bịa chuỗi thay thế ở server). */
  collectionTitle: z.string().nullable(),
  status: memberDueStatusSchema,
  /**
   * Hạn chót của đợt. `null` khi đợt **không đặt hạn** — KHÔNG bịa một ngày ra. Client hiển thị
   * "chưa có hạn"; một ngày bịa sẽ đẻ ra cảnh báo trễ hạn cho khoản chưa bao giờ có hạn.
   */
  dueDate: z.string().datetime().nullable(),
  /**
   * 🔴 US-E6.F8-0784 (@UC0057) — SIÊU DỮ LIỆU KỲ, để sheet "Nộp quỹ" gom 5 dòng cùng tên thành MỘT
   * đợt và sổ xuống từng kỳ. `scheduleId` null = đợt thu một lần (hiển thị phẳng). Client gom bằng
   * join id — không phải phép tính tiền. `isOverdue` do server derive; client không so `dueDate`
   * với đồng hồ máy.
   */
  scheduleId: z.string().uuid().nullable(),
  periodKey: z.string().nullable(),
  periodIndex: z.number().int().positive().nullable(),
  isOverdue: z.boolean(),
};

/**
 * Ba vế TIỀN của một khoản còn thiếu — khai một lần, dùng ở cả nhánh hiện số lẫn nhánh ẩn.
 *
 * `amountMinor` = phần CÒN THIẾU (`outstandingMinorOf` — nguồn chung @UC9659), KHÔNG phải số phải
 * đóng: khoản đóng dở 150k/500k hiện `350000`. Hai vế kia để client vẽ *"đã góp 150k/500k · còn 350k"*
 * mà KHÔNG trừ: `totalMinor` = cả suất, `paidAmountMinor` = đã VÀO SỔ (`collectedMinorOf`, §3.7 trạng
 * thái 3). Bất biến (trừ `waived`): `paidAmountMinor + amountMinor = totalMinor`.
 */
const outstandingMoneyFields = <T extends z.ZodTypeAny>(money: T) => ({
  amountMinor: money,
  totalMinor: money,
  paidAmountMinor: money,
});

/* ---------------------------------------------------------------------------
 * 🔴🔴 US-MOB.F9-1047 (@UC0060 @UC0055 · QĐ người dùng 03/11/2026) — LỜI KHAI BỊ TỪ CHỐI.
 *
 * VÌ SAO PHẢI CÓ: hồ sơ đóng góp của một suất đang kể được "đã đóng" và "còn thiếu", nhưng im lặng
 * hoàn toàn về **lời khai đã bị bác**. Người nộp thấy tiền mình khai biến mất khỏi màn hình mà không
 * có dòng nào nói vì sao — đúng loại im lặng mà một app về NIỀM TIN không được phép có. Dòng này đã
 * tồn tại ở tab "Hoạt động" (`payment.claim_rejected`), nhưng tab đó là của CẢ NHÓM và cuộn trôi;
 * hồ sơ của một người mới là nơi người ta mở ra để đối chiếu.
 *
 * 🔴 KHÔNG PHẢI TIỀN ĐÃ VÀO, VÀ KHÔNG PHẢI NGHĨA VỤ (`CLAUDE.md` §3.7 — ba trạng thái không gộp).
 * Lời khai bị từ chối nằm NGOÀI cả `totalPaidMinor` lẫn `totalOwedMinor`:
 *   · không vào "đã đóng" — chưa đồng nào chạm quỹ, và sẽ không bao giờ (lời khai này đã chết);
 *   · không vào "còn thiếu" — nghĩa vụ gốc vẫn nằm ở `member_dues` và ĐÃ được `outstanding[]` kể
 *     một lần rồi. Cộng thêm ở đây là đếm MỘT khoản nợ HAI lần.
 * ⇒ Danh sách thứ BA, tách bạch, không cộng vào tổng nào. Client chỉ hiển thị.
 *
 * 🔴 CHỈ lời khai bị NGƯỜI KHÁC từ chối. Tự huỷ (`PAYMENT_CLAIM_SELF_CANCEL_REASON`) và huỷ do sửa
 * nhầm KHÔNG phải "bị từ chối" — cùng vị-từ với feed (`activity-feed.service.ts#seekConfirmations`),
 * không phải luật thứ hai.
 *
 * 🔴 KHÔNG PHÂN TRANG, có cap — cùng lẽ với `outstanding[]`: câu hỏi *"lời khai nào của tôi bị bác"*
 * không được phụ thuộc người dùng đã cuộn tới đâu. Sắp MỚI NHẤT TRƯỚC ⇒ thứ bị cắt là thứ cũ nhất.
 * ------------------------------------------------------------------------- */

/** Field KHÔNG-TIỀN của một lời khai bị từ chối — dùng chung cả hai nhánh redaction, để chúng không trôi khỏi nhau. */
const memberContributionRejectedBase = {
  confirmationId: z.string().uuid(),
  /**
   * 🔴 ĐÍCH của lời khai, lấy NGUYÊN nhãn của `payment_confirmations` (`collections.ts`). Client rẽ nhánh theo NHÃN,
   * KHÔNG đoán từ `collectionId === null`: lời khai Quỹ chung và một đợt thu bị xoá tên cho ra cùng một hình dạng null.
   */
  target: paymentConfirmationTargetSchema,
  /** Đợt thu của lời khai; `null` với lời khai Quỹ chung (không thuộc đợt nào). */
  collectionId: z.string().uuid().nullable(),
  /** Tên đợt thu; `null` khi đợt chưa đặt tên HOẶC lời khai Quỹ chung. KHÔNG bịa chuỗi thay thế ở server. */
  collectionTitle: z.string().nullable(),
  /**
   * 🔴 QĐ người dùng 03/11/2026 (vòng 2) — KỲ THỨ MẤY của lịch, để tiêu đề dòng đọc giống tab Hoạt động
   * (*"Quỹ cầu lông · Kỳ 1"*). SERVER đếm (cùng `loadPeriodIndexes` với `data[]`/`outstanding[]` — client
   * KHÔNG đếm kỳ). `null` = đợt thu MỘT LẦN, hoặc lời khai Quỹ chung ⇒ nhãn không có vế "Kỳ n".
   */
  collectionPeriodIndex: z.number().int().positive().nullable(),
  /**
   * Lý do người duyệt gõ. `null` khi họ từ chối không kèm chữ — client KHÔNG dựng dòng "Lý do: " rỗng.
   * Không bao giờ chuỗi rỗng.
   */
  rejectReason: z.string().min(1).nullable(),
};

/** Nhánh CÒN THIẾU của dòng thời gian. Mốc = lúc khoản được TẠO (nghĩa vụ xuất hiện). */
const outstandingEntryBase = {
  kind: z.literal(CONTRIBUTION_ENTRY_KIND.OUTSTANDING),
  ...memberContributionOutstandingBase,
  ...contributionEntryShared,
};

/**
 * Nhánh BỊ TỪ CHỐI của dòng thời gian. Mốc = lúc BỊ TỪ CHỐI (`reviewedAt`), không phải lúc khai —
 * người đọc cần biết quyết định rơi vào lúc nào.
 */
const rejectedEntryBase = {
  kind: z.literal(CONTRIBUTION_ENTRY_KIND.REJECTED),
  ...memberContributionRejectedBase,
  ...contributionEntryShared,
};

/**
 * 🔴🔴 US-MOB.F9-1048 — MỘT hàng của dòng thời gian đóng góp, có SỐ (người xem được phép thấy tiền).
 *
 * Bốn nhánh, KHÔNG phải một khuôn có field nullable. Chúng khác nhau thật: khoản đã đóng có
 * `memberDueId` + `status`; đóng góp Khác có `ledgerTxId` và KHÔNG thuộc đợt nào; khoản còn thiếu có
 * `dueDate` + `isOverdue` + ba vế tiền; lời khai bị bác có `confirmationId` + `rejectReason` và số của
 * nó là số NGƯỜI KHAI GÕ (`claimedAmountMinor`) — chưa đồng nào vào quỹ, nên nó KHÔNG được mang tên
 * `amountMinor` như ba nhánh kia.
 *
 * Union buộc client `switch` ⇒ thêm loại hàng mới là lỗi biên dịch ở mọi nơi vẽ, không phải một nhánh
 * lặng lẽ không hiện.
 */
export const memberContributionEntrySchema = z.discriminatedUnion("kind", [
  z
    .object({
      ...dueEntryBase,
      /**
       * 🔴 Số tiền THỰC SỰ đã vào cho khoản này (`collectedMinorOf` — nguồn chung @UC9659), KHÔNG
       * phải số PHẢI đóng: khoản đóng dở 150k/500k hiện `150000`, không phải `500000`.
       */
      amountMinor: GROUP_MONEY_INTEGER_STRING,
    })
    .strict(),
  z.object({ ...otherIncomeEntryBase, amountMinor: GROUP_MONEY_INTEGER_STRING }).strict(),
  z.object({ ...outstandingEntryBase, ...outstandingMoneyFields(GROUP_MONEY_INTEGER_STRING) }).strict(),
  z
    .object({
      ...rejectedEntryBase,
      /** Số NGƯỜI KHAI đã gõ — KHÔNG phải số phải đóng, và KHÔNG phải tiền đã vào. */
      claimedAmountMinor: GROUP_MONEY_INTEGER_STRING,
    })
    .strict(),
]);

/** Cùng hàng đó khi người xem KHÔNG được phép thấy tiền — `'•••'`, KHÔNG `0` (0 = nói dối về tiền). */
export const redactedMemberContributionEntrySchema = z.discriminatedUnion("kind", [
  z.object({ ...dueEntryBase, amountMinor: z.literal(GROUP_MONEY_REDACTED) }).strict(),
  z.object({ ...otherIncomeEntryBase, amountMinor: z.literal(GROUP_MONEY_REDACTED) }).strict(),
  z
    .object({ ...outstandingEntryBase, ...outstandingMoneyFields(z.literal(GROUP_MONEY_REDACTED)) })
    .strict(),
  z.object({ ...rejectedEntryBase, claimedAmountMinor: z.literal(GROUP_MONEY_REDACTED) }).strict(),
]);

/** Field không-tiền ở cấp response. */
const memberContributionsBase = {
  /** SUẤT được hỏi (echo lại để client chắc chắn đang render đúng người). */
  groupMemberId: z.string().uuid(),
  currency: z.string().length(3),
  pageInfo: z
    .object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    })
    .strict(),
};

/**
 * 🔴 DISCRIMINATED UNION theo `redacted`, KHÔNG phải field optional (mirror `memberListItemSchema` /
 * `myGroupListItemSchema`). Kiểu này làm cho *"vừa ẩn vừa mang số thật"* và *"ẩn nhưng trả `0`"*
 * **không biểu diễn được** — bug không phải bị bắt lúc chạy mà là không viết ra nổi.
 *
 * Cả tổng LẪN từng dòng đều đổi nhánh cùng lúc: ẩn tổng mà để lộ từng dòng thì cộng tay là ra.
 * 🔴 US-E24.F51-0503: `outstanding[]` đi CÙNG nhánh với `data[]` và hai tổng — vì đúng lý lẽ đó, chỉ
 * ở chiều ngược lại (ẩn "đã đóng" mà phơi "còn thiếu" thì trừ ra là biết).
 * 🔴 US-E8.F1-0871: nhánh ẩn nay RỖNG hẳn cả hai danh sách (xem field `data` của nhánh đó).
 */
export const memberContributionsResponseSchema = z.discriminatedUnion("redacted", [
  z
    .object({
      ...memberContributionsBase,
      redacted: z.literal(false),
      /** Σ tiền ĐÃ XÁC NHẬN NHẬN (§3.7 trạng thái 3). Claim "tôi đã chuyển" KHÔNG được cộng vào. */
      totalPaidMinor: GROUP_MONEY_INTEGER_STRING,
      /** Σ còn thiếu — nguồn `FinancialPositionService`, KHÔNG `account_balances`. */
      totalOwedMinor: GROUP_MONEY_INTEGER_STRING,
      /**
       * 🔴 US-MOB.F9-1048 — MỘT mảng cho cả bốn loại hàng, xếp theo `occurredAt` giảm dần, phân trang
       * bằng ĐÚNG MỘT `pageInfo.nextCursor`. Hai mảng rời `outstanding[]`/`rejected[]` đã GỠ: xem
       * docblock `CONTRIBUTION_ENTRY_KIND`.
       *
       * 🔴 `.strict()` ở cấp này là chốt chặn: một server cũ (hoặc một bản vá quên) gửi kèm
       * `outstanding`/`rejected` sẽ bị TỪ CHỐI ở biên, không lặng lẽ bị client bỏ qua.
       */
      data: z.array(memberContributionEntrySchema),
    })
    .strict(),
  z
    .object({
      ...memberContributionsBase,
      redacted: z.literal(true),
      totalPaidMinor: z.literal(GROUP_MONEY_REDACTED),
      totalOwedMinor: z.literal(GROUP_MONEY_REDACTED),
      /**
       * 🔴 US-E8.F1-0871 (@UC0060 @UC0055 · QĐ PO 30/09/2026) — nhánh ẩn **KHÔNG liệt kê khoản nào**
       * (`.max(0)`), kể cả khi mọi số đã là `'•••'`. Che số mà vẫn liệt kê thì người xem đọc được
       * *"chị Lan còn nợ đợt X, hạn Y"* (đúng kênh XEM `managers_only` giấu — §7 P5.3), và dòng "Khác"
       * mang `ledgerTxId` + `createdAt` ghép thẳng vào bảng hoạt động ra số tiền. Kiểu phần tử giữ nguyên
       * để hình dạng wire không đổi với client cũ; chỉ số phần tử bị khoá.
       */
      data: z.array(redactedMemberContributionEntrySchema).max(0),
    })
    .strict(),
]);

export type MemberContributionEntry = z.infer<typeof memberContributionEntrySchema>;
export type MemberContributionsResponse = z.infer<typeof memberContributionsResponseSchema>;

/** Query: cursor opaque optional, limit ∈ [1,50] optional, `kind` optional. Validate tại biên (ZodValidationPipe). */
export const memberContributionsQuerySchema = z.object({
  cursor: z.string().max(MEMBER_CONTRIBUTIONS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(MEMBER_CONTRIBUTIONS_MAX_LIMIT).optional(),
  /**
   * 🔴 US-MOB.F9-1048 — chip đang chọn ở màn «Lịch sử đóng góp». VẮNG ⇒ cả bốn loại (chip *"Tất cả"*).
   *
   * Nó là bộ lọc của SERVER, không phải của client, và đó là điểm chính: lọc ở client thì mỗi lượt tải
   * thêm vẫn kéo về trọn các loại đang bị ẩn rồi bỏ đi, và `hasNext` nói về một tập khác với tập đang
   * hiện ⇒ chip nào không phải *"Đã đóng"* sẽ không bao giờ tải thêm được. Lọc ở đây thì mỗi chip là
   * một danh sách có cursor riêng, đủ nghĩa.
   */
  kind: memberContributionFilterKindSchema.optional(),
});

export type MemberContributionsQuery = z.infer<typeof memberContributionsQuerySchema>;

/**
 * 🔴 CONFLICT-CODE-NET-LOWS (@UC0011) — 409: tạo nhóm đụng unique `one_owner_per_group` / `(groupId, primaryUserId)` (P2002). Không xảy ra ở
 * luồng tạo bình thường (id nhóm mới) — tồn tại để KHÔNG nuốt lỗi unique thành 500. Không `params`. Trước đây 409 chuỗi trần.
 */
export const GROUP_ALREADY_HAS_OWNER_CODE = "GROUP_ALREADY_HAS_OWNER" as const;
/** `detail` của `GROUP_ALREADY_HAS_OWNER_CODE` — giữ NGUYÊN câu cũ. */
export const GROUP_ALREADY_HAS_OWNER_MESSAGE = "Nhóm đã có chủ sở hữu.";
